import { useEffect, useRef, useState } from "react";
import DatePicker, { registerLocale } from "react-datepicker";
import he from "date-fns/locale/he";
import "react-datepicker/dist/react-datepicker.css";
import "./Settings.css";
import EventDatePicker from "../EventDatePicker";
import { getLocalIsoDate } from "../../Utils/eventDetails";
import readXlsxFile from "read-excel-file";
import {
  addContactTransactional,
  addDocumentData,
  auth,
  claimMessageGift,
  deleteContactTransactional,
  getDocument,
  getDeliveryRecords,
  mergeImportedContacts,
  updateContactTransactional,
} from "../../firebase";
import moment from "moment";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import { determinStatus, exportRsvpSummaryToSpreadsheet, exportToSpreadsheet, isBankEmpty } from "../../Utils";
import { parseContactImportRows } from "../../Utils/contactImport";
import { getRecordUsage, recordOverageMessage } from "../../Utils/recordLimit";
import { getFreeContactsVisibility, isFreeContactDeletionLocked, FREE_DELETION_MESSAGE } from "../../Utils/freeRsvpVisibility";
import {
  buildWhatsAppContactBotUrl,
  canUseWhatsAppContactImport,
  shouldOfferAutopilotPurchase,
  WHATSAPP_CONTACT_COMPLETION_PHRASE,
} from "../../Utils/whatsappContactImport";
import CommercePackagePicker from "../AutopilotSetup/CommercePackagePicker";
import MessageScheduleList from "../MessageScheduleList";
import MessagePreview from "../MessagePreview";
import { callEventAutopilot } from "../../Utils/eventAutopilotApi";
import RsvpAdditionalOptions from "../RsvpAdditionalOptions";
import {
  finalizeRsvpDetails,
  getAttendeeExactDish,
  getAttendeePreferenceLabels,
  getContactRsvpMissingAnswers,
  getContactPreferenceCount,
  getEnabledFoodOptions,
  getEventRsvpSummary,
  getFoodAnswerState,
  hasEnabledRsvpOptions,
  hasMissingRsvpAnswers,
  normalizeRsvpDetails,
  normalizeRsvpOptions,
  resizeRsvpDetails,
} from "../../Utils/rsvpOptions";
import { getVoiceWorkflowCompletionState, isVoiceRsvpRecorded } from "../../Utils/voiceHistory";

registerLocale("he", he);

/** Firestore + Cloud Function schedule format (unchanged for backend). */
const STORAGE_DATE_FORMAT = "YYYY-MM-DD HH:mm";

/** Legacy messages[] scheduling is being retired (helloWorld scheduler); history and cancellation stay available. */
export const LEGACY_MESSAGE_SCHEDULING_CLOSED = true;
const LEGACY_SCHEDULING_CLOSED_TEXT = "האירוע הסתיים - לא ניתן לתזמן הודעות נוספות.";

const WhatsAppIcon = ({ size = 18 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
    focusable="false"
  >
    <path d="M12.04 2A9.84 9.84 0 0 0 3.6 16.9L2 22l5.25-1.55A9.96 9.96 0 1 0 12.04 2Zm0 17.96a8.07 8.07 0 0 1-4.12-1.13l-.3-.18-3.11.92.94-3.03-.2-.31a8.02 8.02 0 1 1 6.79 3.73Zm4.42-6.04c-.24-.12-1.43-.7-1.65-.79-.22-.08-.38-.12-.54.12-.16.24-.62.79-.76.95-.14.16-.28.18-.52.06-.24-.12-1.02-.38-1.94-1.2a7.25 7.25 0 0 1-1.34-1.67c-.14-.24-.02-.37.1-.49.11-.11.24-.28.36-.42.12-.14.16-.24.24-.4.08-.16.04-.3-.02-.42-.06-.12-.54-1.3-.74-1.78-.2-.47-.4-.4-.54-.41h-.46c-.16 0-.42.06-.64.3-.22.24-.84.82-.84 2s.86 2.32.98 2.48c.12.16 1.69 2.58 4.1 3.62.57.25 1.02.4 1.37.51.58.18 1.1.16 1.51.1.46-.07 1.43-.59 1.63-1.15.2-.56.2-1.04.14-1.14-.06-.1-.22-.16-.46-.28Z" />
  </svg>
);

const createMessageId = () => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `message-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
};

const MESSAGE_PURPOSES = {
  invitation: {
    label: "שליחת הזמנה ואישור הגעה",
    description: "ההודעה תישלח עם תמונת ההזמנה ולינק לאישור ההגעה.",
    recommendation: "מומלץ לשליחה הראשונה לקבוצה: כולם או ממתינים לתשובה/לא יודעים.",
    recommendedGroup: "all",
    withLink: true,
  },
  unansweredReminder: {
    label: "תזכורת למי שלא ענה",
    description: "התזכורת תישלח עם תמונת ההזמנה ולינק לאישור ההגעה.",
    recommendation: "מומלץ לשלוח כשבוע לאחר השליחה הראשונה לקבוצה: ממתינים לתשובה/לא יודעים.",
    recommendedGroup: "pending",
    withLink: true,
  },
  confirmedReminder: {
    label: "תזכורת לקראת האירוע",
    description: "התזכורת תישלח עם תמונת ההזמנה וללא לינק לאישור הגעה. ניתן גם להקליד טקסט חופשי בהודעות SMS בלבד",
    recommendation: "מומלץ לשלוח יום לפני האירוע לקבוצה: מוזמנים שאישרו.",
    recommendedGroup: "approved",
    withLink: false,
  },
};

const normalizePhone = (value) => String(value || "").replace(/\D/g, "");

const VOICE_CALL_STATUS_LABELS = {
  initiated: "החיוג התחיל",
  ringing: "מצלצל",
  answered: "נענה",
  "in-progress": "השיחה פעילה",
  completed: "השיחה הסתיימה",
  busy: "תפוס",
  failed: "החיוג נכשל",
  "no-answer": "אין מענה",
  canceled: "החיוג בוטל",
};

const voiceHistoryLabels = (record) => {
  const completionState = getVoiceWorkflowCompletionState(record);
  return [
    "בוצע ניסיון חיוג",
    record.callStatus ? (VOICE_CALL_STATUS_LABELS[record.callStatus] || record.callStatus) : null,
    record.answered === true ? "השיחה נענתה" : null,
    completionState === "completed" ? "תהליך המענה הושלם ואושר"
      : completionState === "incomplete" ? "תהליך המענה לא הושלם" : null,
    isVoiceRsvpRecorded(record) ? "תשובת אישור הגעה נרשמה" : null,
  ].filter(Boolean);
};

const VOICE_RSVP_STATUS_LABELS = {
  approved: "אישרו הגעה",
  pending: "טרם החליטו",
  notComing: "לא מגיעים",
};

const formatVoiceHistoryDate = (value) => {
  const date = value?.toDate ? value.toDate() : new Date(value || 0);
  if (!Number.isFinite(date.getTime())) return "";
  return new Intl.DateTimeFormat("he-IL", {
    timeZone: "Asia/Jerusalem",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).format(date);
};

const extractUploadcareUUID = (url = "") => {
  const match = String(url || "").match(/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i);
  return match ? match[1] : null;
};

const buildWizardMessage = ({
  purpose,
  channel = "whatsapp",
  eventTitle = "",
  invitationLink = "",
  invitationImageUrl = "",
  includeTable = false,
  includeTransportation = false,
  transportationDetails = "",
  sampleTable = "12",
}) => {
  const normalizedEventTitle = channel === "sms" ? String(eventTitle || "").trim() : "";
  const titledEvent = normalizedEventTitle ? `האירוע של ${normalizedEventTitle}` : "האירוע";
  if (purpose === "unansweredReminder") {
    return `תזכורת קטנה 😊 ${titledEvent} מתקרב ועדיין לא קיבלנו את אישור הגעתכם.\nלאישור הגעה: ${invitationLink}\nנשמח לקבל את עדכונכם.`;
  }
  if (purpose === "confirmedReminder") {
    const details = [
      includeTable ? `מספר השולחן שלכם: ${sampleTable}.` : "",
      includeTransportation ? `ההסעה תצא מ־${transportationDetails || "[פרטי ההסעה]"}.` : "",
    ].filter(Boolean).join(" ");
    const reminder = `תזכורת לאירוע 😊 האירוע שאישרתם את הגעתכם אליו מתקרב.${details ? ` ${details}` : ""} נשמח לראותכם.`;
    return channel === "sms" && invitationImageUrl
      ? `${reminder}\nלינק לפרטי ההזמנה לאירוע:\n${invitationImageUrl}`
      : reminder;
  }
  const invitationOpening = normalizedEventTitle ? `הוזמנתם לאירוע של ${normalizedEventTitle}\n` : "";
  return `${invitationOpening}אנא אשרו את הגעתכם בלינק:\n${invitationLink}\n- לחצו לפתיחה`;
};

const getPartySize = (contact) => {
  const parsed = parseInt(String(contact?.howMany || "1"), 10);
  return Number.isNaN(parsed) || parsed < 1 ? 1 : parsed;
};

const Timing = ({ handleChange, currentName, checked, isDisabled, demoAllow = false, counts = {} }) => {
  useEffect(() => {
    let elems = document.getElementsByName(currentName);
    elems.forEach((el) => {
      if (el.value === checked) {
        el.click();
      }
    });
  }, [checked]);
  const fmt = (n) => (n != null ? <span className="badge bg-secondary ms-1" style={{ fontSize: "11px" }}>{n} אנשי קשר</span> : null);
  return (
    <div className="col-12 d-flex flex-column justify-content-center align-items-center align-content-center" onChange={(e) => handleChange(e.target.value)}>
      <div className="fs-3 text-muted">לאיזו קבוצה לשלוח?</div>
      <div className="col-12 d-flex flex-row flex-wrap justify-content-center recipient-group-row gap-2 p-2">
        {[
          { label: "לכולם", value: "all", count: counts.all },
          { label: "רק למי שלא מגיע", value: "notComing", count: counts.notComing },
          { label: "מוזמנים שאישרו", value: "approved", count: counts.approved },
          { label: "ממתינים לתשובה/לא יודעים", value: "pending", count: counts.pending },
        ].map((opt) => (
          <label
            key={opt.value}
            className={`recipient-group-pill d-flex flex-column justify-content-center align-items-center text-center border rounded pointer ${
              checked === opt.value ? "recipient-group-pill--selected" : "recipient-group-pill--default"
            }`}
            style={{ cursor: isDisabled ? "not-allowed" : "pointer", opacity: isDisabled ? 0.6 : 1 }}
          >
            <span className="recipient-group-pill__label fw-semibold mb-1">{opt.label}</span>
            {fmt(opt.count)}
            <input
              disabled={isDisabled}
              data-demo-allow={demoAllow ? "true" : undefined}
              className="form-check-input mt-2"
              type="radio"
              name={currentName}
              value={opt.value}
              style={{ display: "none" }}
            />
          </label>
        ))}
      </div>
    </div>
  );
};

const PURCHASE_MESSAGES_URL = "https://api.whatsapp.com/send?phone=9720586665517&text=%D7%A9%D7%9C%D7%95%D7%9D%2C%20%D7%90%D7%A0%D7%99%20%D7%A8%D7%95%D7%A6%D7%94%20%D7%9C%D7%A8%D7%9B%D7%95%D7%A9%20%D7%91%D7%A0%D7%A7%20%D7%94%D7%95%D7%93%D7%A2%D7%95%D7%AA%20%D7%9C%D7%90%D7%99%D7%A9%D7%95%D7%A8%D7%99%20%D7%94%D7%92%D7%A2%D7%94";

const PurchaseMessagesLink = () => (
  <div className="message-gift__purchase">
    <a
      href={PURCHASE_MESSAGES_URL}
      target="_blank"
      rel="noopener noreferrer"
      className="message-gift__purchase-link"
    >
      <img
        src="https://www.flashback.co.il/he/wp-content/uploads/2025/11/%D7%9B%D7%A4%D7%AA%D7%95%D7%A8-%D7%99%D7%A6%D7%99%D7%A8%D7%AA-%D7%A7%D7%A9%D7%A8-%D7%95%D7%95%D7%90%D7%98%D7%A1%D7%90%D7%A4.png.webp"
        alt="רכישת הודעות"
        className="message-gift__purchase-image"
      />
      <span>אני רוצה לרכוש הודעות וואטסאפ/SMS</span>
    </a>
  </div>
);

const NoBankBanner = ({ giftEligible, giftDismissed, isClaiming, onClaim, onDismiss }) => (
  <div className="message-gift" dir="rtl">
    {giftEligible && !giftDismissed ? (
      <section className="message-gift__card" aria-labelledby="message-gift-title">
        <span className="message-gift__spark message-gift__spark--one" aria-hidden="true">✦</span>
        <span className="message-gift__spark message-gift__spark--two" aria-hidden="true">✦</span>
        <div className="message-gift__icon" aria-hidden="true">🎁</div>
        <span className="message-gift__eyebrow">מתנה קטנה לאירוע שלך</span>
        <h3 id="message-gift-title">רוצים לראות איך המערכת שולחת הודעות לאורחים במקומכם?</h3>
        <p>
          תנו לנו לעשות קצת מהעבודה בשבילכם: קבלו מאיתנו במתנה
          <strong> 10 הודעות WhatsApp ו־10 הודעות SMS אוטומטיות</strong> — ללא עלות וללא התחייבות.
        </p>
        <small>
          הזדמנות חד־פעמית לגלות כמה זמן ושקט
          <br />
          המערכת יכולה לחסוך לכם.
        </small>
        <div className="message-gift__actions">
          <button type="button" className="message-gift__claim" disabled={isClaiming} onClick={onClaim}>
            {isClaiming ? "המתנה בדרך אליכם…" : "כן! אני רוצה לקבל את המתנה 🎉"}
          </button>
          <button type="button" className="message-gift__dismiss" disabled={isClaiming} onClick={onDismiss}>
            לא תודה, אמשיך לשלוח לאורחים בעצמי
          </button>
        </div>
      </section>
    ) : (
      <div className="message-gift__empty">אין כרגע יתרת הודעות זמינה.</div>
    )}
    <PurchaseMessagesLink />
  </div>
);

const ChannelIcon = ({ channel }) => (
  <span className={`channel-toggle__icon channel-toggle__icon--${channel}`} aria-hidden="true">
    {channel === "whatsapp" ? (
      <svg viewBox="0 0 24 24"><path d="M19.1 4.9A9.8 9.8 0 0 0 3.7 16.7L2.3 22l5.4-1.4A9.8 9.8 0 0 0 22 11.9a9.7 9.7 0 0 0-2.9-7Zm-7 15.2a8 8 0 0 1-4.1-1.1l-.3-.2-3.2.8.9-3.1-.2-.3a8 8 0 1 1 6.9 3.9Zm4.4-6c-.2-.1-1.4-.7-1.7-.8-.2-.1-.4-.1-.6.1-.2.3-.6.8-.8 1-.1.2-.3.2-.5.1-1.5-.7-2.5-1.3-3.5-3-.3-.5.3-.5.8-1.6.1-.2 0-.4 0-.5l-.8-1.9c-.2-.5-.4-.4-.6-.4h-.5c-.2 0-.5.1-.7.3-.2.3-1 1-1 2.4s1 2.8 1.2 3c.1.2 2 3.1 4.9 4.3 1.8.8 2.5.8 3.4.7 1-.2 1.4-1.2 1.6-1.7.2-.5.2-1 .1-1.1-.2-.2-.4-.2-.7-.4Z" /></svg>
    ) : (
      <svg viewBox="0 0 24 24"><path d="M3 5h18v14H3V5Zm2 2v.4l7 5.1 7-5.1V7H5Zm14 10V9.9l-7 5.1-7-5.1V17h14Z" /></svg>
    )}
  </span>
);

const ChannelButtonContent = ({ channel, balance, selected = false }) => (
  <>
    <ChannelIcon channel={channel} />
    <span className="channel-toggle__copy">
      <strong>{channel === "whatsapp" ? "שליחת וואטסאפ" : "שליחת SMS"}</strong>
      <small>נותרו {balance} הודעות</small>
      {selected && <span className="channel-toggle__selected">✓ נבחר</span>}
    </span>
  </>
);

const RoundChannelToggle = ({ value, onChange, disabled, smsAvailable, whatsappAvailable, smsBank, whatsAppBank, giftEligible, giftDismissed, isClaimingGift, onClaimGift, onDismissGift }) => {
  if (!smsAvailable && !whatsappAvailable) {
    return (
      <NoBankBanner
        giftEligible={giftEligible}
        giftDismissed={giftDismissed}
        isClaiming={isClaimingGift}
        onClaim={onClaimGift}
        onDismiss={onDismissGift}
      />
    );
  }
  if (smsAvailable && !whatsappAvailable) {
    return (
      <div className="channel-toggle-group">
        <div className="channel-toggle__heading">בחרו סוג הודעה</div>
        <div className="channel-toggle">
        <span className="channel-toggle__button channel-toggle__button--sms channel-toggle__button--active pe-none" tabIndex={-1} aria-current="true">
          <ChannelButtonContent channel="sms" balance={smsBank} selected />
        </span>
        </div>
      </div>
    );
  }
  if (!smsAvailable && whatsappAvailable) {
    return (
      <div className="channel-toggle-group">
        <div className="channel-toggle__heading">בחרו סוג הודעה</div>
        <div className="channel-toggle">
        <span className="channel-toggle__button channel-toggle__button--whatsapp channel-toggle__button--active pe-none" tabIndex={-1} aria-current="true">
          <ChannelButtonContent channel="whatsapp" balance={whatsAppBank} selected />
        </span>
        </div>
      </div>
    );
  }
  return (
    <div className="channel-toggle-group">
      <div className="channel-toggle__heading">בחרו סוג הודעה</div>
      <div className="channel-toggle" role="group" aria-label="בחירת סוג הודעה">
      <button
        type="button"
        className={`channel-toggle__button channel-toggle__button--sms ${value === "sms" ? "channel-toggle__button--active" : ""}`}
        disabled={disabled}
        aria-pressed={value === "sms"}
        onClick={() => onChange("sms")}
      >
        <ChannelButtonContent channel="sms" balance={smsBank} selected={value === "sms"} />
      </button>
      <button
        type="button"
        className={`channel-toggle__button channel-toggle__button--whatsapp ${value === "whatsapp" ? "channel-toggle__button--active" : ""}`}
        disabled={disabled}
        aria-pressed={value === "whatsapp"}
        onClick={() => onChange("whatsapp")}
      >
        <ChannelButtonContent channel="whatsapp" balance={whatsAppBank} selected={value === "whatsapp"} />
      </button>
      </div>
    </div>
  );
};

const MessagePurposeWizard = ({ value, onChange, disabled = false }) => {
  const [isOpen, setIsOpen] = useState(!value);
  const selectedPurpose = MESSAGE_PURPOSES[value];

  useEffect(() => {
    if (!value) setIsOpen(true);
  }, [value]);

  const selectPurpose = (key) => {
    onChange(key);
    setIsOpen(false);
  };

  return (
    <section className={`message-purpose ${isOpen ? "message-purpose--open" : "message-purpose--closed"}`} dir="rtl">
      <button
        type="button"
        id="message-purpose-title"
        className="message-purpose__toggle"
        aria-expanded={isOpen}
        aria-controls="message-purpose-options"
        onClick={() => setIsOpen((open) => !open)}
      >
        <strong>{selectedPurpose ? `מטרת הודעה: ${selectedPurpose.label}` : "מה מטרת ההודעה?"}</strong>
        <span className={`message-purpose__arrow ${isOpen ? "message-purpose__arrow--open" : ""}`} aria-hidden="true">▼</span>
      </button>

      <div
        id="message-purpose-options"
        className="message-purpose__options-collapse"
        aria-hidden={!isOpen}
      >
        <div className="message-purpose__options">
          {Object.entries(MESSAGE_PURPOSES).map(([key, option]) => (
            <button
              key={key}
              type="button"
              className={`message-purpose__option ${value === key ? "message-purpose__option--selected" : ""}`}
              disabled={disabled || !isOpen}
              aria-pressed={value === key}
              onClick={() => selectPurpose(key)}
            >
              <span className="message-purpose__check" aria-hidden="true">{value === key ? "✓" : ""}</span>
              <strong>{option.label}</strong>
              <small className="message-purpose__recommendation">{option.recommendation}</small>
              <span className="message-purpose__description">{option.description}</span>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
};

export const RsvpEventSummary = ({ contacts, options: optionsValue, addAlertText, hideAssumptionNotes = false }) => {
  const options = normalizeRsvpOptions(optionsValue);
  const [isOpen, setIsOpen] = useState(false);
  const [exportInProgress, setExportInProgress] = useState("");
  const exportInProgressRef = useRef(false);
  const summaryContentRef = useRef(null);
  if (!hasEnabledRsvpOptions(options)) return null;
  const summary = getEventRsvpSummary(contacts, options);
  const { totals, missing, dishCombinations } = summary;
  const transportationAssumption = !hideAssumptionNotes && missing.transportation > 0
    ? `${missing.transportation} הזמנות ללא תשובה חושבו כלא צריכות הסעה`
    : "";
  const items = [
    { key: "transportationSeats", label: "מקומות בהסעה", value: totals.transportationSeats, note: transportationAssumption, show: options.transportation.enabled },
    { key: "male", label: "גברים", value: totals.male, show: options.gender.enabled },
    { key: "female", label: "נשים", value: totals.female, show: options.gender.enabled },
  ].filter((item) => item.show);
  const notices = [
    options.gender.enabled && missing.gender > 0
      ? `ל-${missing.gender} אורחים חסרה בחירת גבר או אישה`
      : "",
  ].filter(Boolean);

  const runExport = async (type, task) => {
    if (exportInProgressRef.current) return;
    exportInProgressRef.current = true;
    setExportInProgress(type);
    const startedAt = Date.now();
    try {
      await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
      await task();
    } catch (error) {
      addAlertText({ mode: "danger", text: "לא הצלחנו ליצור את הקובץ. נסו שוב." });
    } finally {
      const remaining = Math.max(0, 350 - (Date.now() - startedAt));
      if (remaining > 0) await new Promise((resolve) => setTimeout(resolve, remaining));
      exportInProgressRef.current = false;
      setExportInProgress("");
    }
  };

  const downloadSummaryPdf = () => runExport("pdf", async () => {
    const source = summaryContentRef.current;
    if (!source) throw new Error("Summary content is unavailable");
    const report = source.cloneNode(true);
    report.querySelector(".rsvp-event-summary__exports")?.remove();
    Object.assign(report.style, {
      position: "fixed",
      right: "-10000px",
      top: "0",
      width: "980px",
      margin: "0",
      background: "#fff",
    });
    document.body.appendChild(report);
    try {
      const canvas = await html2canvas(report, { scale: 2, backgroundColor: "#ffffff" });
      const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      const margin = 12;
      const maxWidth = pdf.internal.pageSize.getWidth() - margin * 2;
      const maxHeight = pdf.internal.pageSize.getHeight() - margin * 2;
      const naturalHeight = (canvas.height * maxWidth) / canvas.width;
      const imageHeight = Math.min(naturalHeight, maxHeight);
      const imageWidth = naturalHeight > maxHeight ? (canvas.width * maxHeight) / canvas.height : maxWidth;
      const horizontalOffset = (pdf.internal.pageSize.getWidth() - imageWidth) / 2;
      pdf.addImage(canvas.toDataURL("image/jpeg", 0.94), "JPEG", horizontalOffset, margin, imageWidth, imageHeight);
      pdf.save("סיכום העדפות אורחים.pdf");
    } finally {
      report.remove();
    }
  });

  const downloadSummaryExcel = () => runExport("excel", () =>
    exportRsvpSummaryToSpreadsheet({ summary, options }, "סיכום העדפות אורחים")
  );

  return (
    <section className="rsvp-event-summary schedule-list schedule-list--sent" dir="rtl">
      <button
        type="button"
        className="schedule-list__header schedule-list__header--toggle"
        aria-expanded={isOpen}
        aria-controls="rsvp-event-summary-panel"
        onClick={() => setIsOpen((current) => !current)}
      >
        <h2 id="rsvp-event-summary-title">סיכום העדפות אורחים</h2>
        <span>{totals.approvedPeople} אורחים</span>
      </button>
      <button
        type="button"
        className="schedule-list__dropdown-bar"
        aria-label={isOpen ? "סגירת סיכום העדפות אורחים" : "פתיחת סיכום העדפות אורחים"}
        aria-expanded={isOpen}
        aria-controls="rsvp-event-summary-panel"
        onClick={() => setIsOpen((current) => !current)}
      >
        <span
          className={`schedule-list__dropdown-arrow ${isOpen ? "schedule-list__dropdown-arrow--open" : ""}`}
          aria-hidden="true"
        >▼</span>
      </button>
      <div
        id="rsvp-event-summary-panel"
        className={`rsvp-event-summary__panel${isOpen ? " rsvp-event-summary__panel--open" : ""}`}
        aria-hidden={!isOpen}
      >
        <div className="rsvp-event-summary__panel-inner">
          <div className="rsvp-event-summary__content" ref={summaryContentRef}>
        <div className="rsvp-event-summary__exports">
          <button type="button" disabled={Boolean(exportInProgress)} onClick={downloadSummaryExcel}>
            <span className="rsvp-event-summary__export-icon rsvp-event-summary__export-icon--excel" aria-hidden="true">X</span>
            הורדת Excel
          </button>
          <button type="button" disabled={Boolean(exportInProgress)} onClick={downloadSummaryPdf}>
            <span className="rsvp-event-summary__export-icon rsvp-event-summary__export-icon--pdf" aria-hidden="true">PDF</span>
            הורדת PDF
          </button>
        </div>
        {items.length > 0 && (
          <div className="rsvp-event-summary__totals">
            {items.map((item) => (
              <div key={item.key}>
                <strong>{item.value}</strong>
                <span>{item.label}</span>
                {item.note && <small className="rsvp-event-summary__cube-note">⚠ {item.note}</small>}
              </div>
            ))}
          </div>
        )}
        {dishCombinations.length > 0 && (
          <div className="rsvp-event-summary__dishes">
            <h4>מנות מדויקות להכנה</h4>
            <div className="rsvp-event-summary__totals">
              {dishCombinations.map((dish) => (
                <div key={dish.label}>
                  <strong>{dish.count}</strong>
                  <span>{dish.label}</span>
                  {!hideAssumptionNotes && dish.assumedCount > 0 && (
                    <small className="rsvp-event-summary__cube-note">
                      ⚠ מתוכן {dish.assumedCount} חושבו כברירת מחדל בגלל תשובה חסרה
                    </small>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
        {notices.length > 0 && (
          <div className="rsvp-event-summary__notices">
            {notices.map((notice) => <span key={notice}>⚠ {notice}</span>)}
          </div>
        )}
          </div>
        </div>
      </div>
      {exportInProgress && (
        <div className="dashboard-export-overlay" role="status" aria-live="assertive" aria-busy="true">
          <div className="dashboard-export-overlay__dialog" dir="rtl">
            <span className="dashboard-export-overlay__spinner" aria-hidden="true" />
            <strong>{exportInProgress === "pdf" ? "מכין את קובץ ה־PDF…" : "מכין את קובץ האקסל…"}</strong>
            <span>הקובץ יתחיל לרדת מיד כשהוא יהיה מוכן.</span>
          </div>
        </div>
      )}
    </section>
  );
};

const Settings = ({ addAlertText, docID, activeTab, onTabChange, isReadOnly = false, eventAccessData }) => {
  const [excelImportResult, setExcelImportResult] = useState(null);
  const [isImportingExcel, setIsImportingExcel] = useState(false);
  const [contactList, setContactList] = useState([]);
  const [isContactsLoading, setIsContactsLoading] = useState(true);
  const [total, setTotal] = useState("");
  const [smsBank, setSmsBank] = useState(0);
  const [whatsAppBank, setWhatsAppBank] = useState("0");
  const [messageGiftEligible, setMessageGiftEligible] = useState(false);
  const [messageGiftDismissed, setMessageGiftDismissed] = useState(false);
  const [isClaimingMessageGift, setIsClaimingMessageGift] = useState(false);
  const [showGiftCelebration, setShowGiftCelebration] = useState(false);
  const [confirmDeleteMsgIdx, setConfirmDeleteMsgIdx] = useState(null);
  const [seatingTables, setSeatingTables] = useState([]);

  // Contact management panel
  const [newContactName, setNewContactName] = useState("");
  const [newContactPhone, setNewContactPhone] = useState("");
  const [newContactHowMany, setNewContactHowMany] = useState("1");
  const [newContactStatus, setNewContactStatus] = useState("pending");
  const [showAddContactForm, setShowAddContactForm] = useState(false);
  const [deleteConfirmIdx, setDeleteConfirmIdx] = useState(null);
  const [tableRemovalPrompt, setTableRemovalPrompt] = useState(null);
  const [nameEdit, setNameEdit] = useState(null);   // {idx, value, saving} while a guest name is being edited
  const [rowEdits, setRowEdits] = useState({});   // idx -> {status, howMany, tableNum} unsaved per-row
  const [showSavedNotice, setShowSavedNotice] = useState(false);
  const contactListRef = useRef([]);
  const rowSaveTimersRef = useRef({});
  const savedNoticeTimerRef = useRef(null);
  contactListRef.current = contactList;
  const [contactSearch, setContactSearch] = useState("");
  const [contactSort, setContactSort] = useState("");
  const [latestAddedContactPhone, setLatestAddedContactPhone] = useState("");
  const [rsvpOptions, setRsvpOptions] = useState(() => normalizeRsvpOptions());
  const [showExcelModal, setShowExcelModal] = useState(false);
  const [showWhatsappContactImportModal, setShowWhatsappContactImportModal] = useState(false);
  const [messageBankPurchased, setMessageBankPurchased] = useState(false);
  const [messageGiftClaimed, setMessageGiftClaimed] = useState(false);
  const [recordEntitlement, setRecordEntitlement] = useState({ autopilotAssignment: null, recordLimit: null, rsvpAccess: null });
  const [guestOptionsModal, setGuestOptionsModal] = useState(null);
  const [isSavingGuestOptions, setIsSavingGuestOptions] = useState(false);
  const [openContactPreferencesIdx, setOpenContactPreferencesIdx] = useState(null);

  useEffect(() => {
    if (!eventAccessData) return;
    setRecordEntitlement({
      autopilotAssignment: eventAccessData.autopilotAssignment || null,
      recordLimit: eventAccessData.recordLimit ?? null,
      rsvpAccess: eventAccessData.rsvpAccess || null,
    });
  }, [eventAccessData]);

  // Message log
  const [messages, setMessages] = useState([]);
  const [messageSummary, setMessageSummary] = useState(null);
  const [isSubmittingMessage, setIsSubmittingMessage] = useState(false);

  // Delivery records (per-guest sent counts)
  const [deliveryRecords, setDeliveryRecords] = useState({ sms: [], wa: [], voice: [], skipped: [], messageResults: [] });

  // WhatsApp preview
  const [invitationImageUrl, setInvitationImageUrl] = useState("");
  const [eventTitle, setEventTitle] = useState("");

  // Single form state
  const defaultSmsDate = () => new Date();
  const [smsDate, setSmsDate] = useState(defaultSmsDate);
  const [smsSendTo, setSmsSendTo] = useState("all");
  const [roundChannel, setRoundChannel] = useState("whatsapp");
  const [messagePurpose, setMessagePurpose] = useState("");
  const [includeTable, setIncludeTable] = useState(false);
  const [includeTransportation, setIncludeTransportation] = useState(false);
  const [isFreeTextSms, setIsFreeTextSms] = useState(false);
  const [freeTextSms, setFreeTextSms] = useState("");
  const confirmedReminderOptionsRef = useRef(null);
  const messageSummaryBodyRef = useRef(null);

  const smsBankAvailable = !isBankEmpty(smsBank);
  const whatsAppBankAvailable = !isBankEmpty(whatsAppBank);
  const hasPositiveLegacyMessageBank = smsBankAvailable || whatsAppBankAvailable;
  const packageRecordEntitled = Boolean(
    recordEntitlement.autopilotAssignment?.packageId
    && recordEntitlement.autopilotAssignment?.templateSnapshot
  );
  const legacySchedulingClosed = LEGACY_MESSAGE_SCHEDULING_CLOSED && !packageRecordEntitled;
  // Only events that actually used legacy messaging see the notice; new FREE/MANUAL events see neither notice nor form.
  const showLegacyClosedNotice = legacySchedulingClosed && (hasPositiveLegacyMessageBank || messages.length > 0);
  const showAutopilotPurchase = shouldOfferAutopilotPurchase({
    packageRecordEntitled,
    isReadOnly,
    rsvpAccess: recordEntitlement.rsvpAccess,
    smsBank,
    whatsAppBank,
  });
  const eventOwnerEmail = auth.currentUser?.email || "";
  const whatsappContactImportEligible = canUseWhatsAppContactImport({
    messageBankPurchased,
    messageGiftClaimed,
    hasMessageHistory: messages.length > 0,
    smsBank,
    whatsAppBank,
    packageRecordEntitled,
  });
  const whatsappContactBotUrl = buildWhatsAppContactBotUrl({ docID, email: eventOwnerEmail });
  const transportationDetails = String(rsvpOptions.transportation.departureDetails || "").trim();
  const transportationAvailable = rsvpOptions.transportation.enabled && Boolean(transportationDetails);

  const freeContactsVisibility = getFreeContactsVisibility(recordEntitlement, contactList);
  const visibleContactList = freeContactsVisibility.visibleContacts;
  const visibleContactSet = new Set(visibleContactList);
  const freeDeletionBlocked = isFreeContactDeletionLocked(recordEntitlement, contactList);
  const groupCounts = {
    all: visibleContactList.length,
    notComing: visibleContactList.filter(c => c.status === "notComing").length,
    approved: visibleContactList.filter(c => c.status === "approved").length,
    pending: visibleContactList.filter(c => !["approved", "notComing"].includes(c.status)).length,
  };

  const getBankShortageWarnings = () => {
    let smsBalance = parseInt(String(smsBank || "0"), 10) || 0;
    let whatsAppBalance = parseInt(String(whatsAppBank || "0"), 10) || 0;

    return messages
      .filter((m) => m.status !== "cancelled" && m.status !== "error" && moment(m.timeToSend).isAfter(moment()))
      .map((m, index) => {
        const count = contactList.filter((c) => m.toGroup === "all" || c.status === m.toGroup).length;
        const reserved = Math.max(0, parseInt(m.recipientCount, 10) || 0);
        const extraNeeded = Math.max(0, count - reserved);
        const availableBank = m.channel === "whatsapp" ? whatsAppBalance : smsBalance;
        const extraCovered = Math.min(extraNeeded, availableBank);
        const missing = Math.max(0, extraNeeded - extraCovered);

        if (m.channel === "whatsapp") {
          whatsAppBalance -= extraCovered;
        } else {
          smsBalance -= extraCovered;
        }

        return { index, message: m, missing };
      })
      .filter(({ missing }) => missing > 0);
  };

  // Sync channel default when bank availability changes
  useEffect(() => {
    const smsOk = !isBankEmpty(smsBank);
    const waOk = !isBankEmpty(whatsAppBank);
    setRoundChannel((c) => {
      if (!smsOk && waOk) return "whatsapp";
      if (smsOk && !waOk) return "sms";
      return c;
    });
  }, [smsBank, whatsAppBank]);

  useEffect(() => {
    if (seatingTables.length === 0) setIncludeTable(false);
    if (!transportationAvailable) setIncludeTransportation(false);
  }, [seatingTables.length, transportationAvailable]);

  useEffect(() => {
    if (messagePurpose !== "confirmedReminder" || typeof window === "undefined") return undefined;
    if (!window.matchMedia?.("(max-width: 575px)").matches) return undefined;

    let scrollFrameId;
    const measureTimerId = window.setTimeout(() => {
      const panel = confirmedReminderOptionsRef.current;
      if (!panel) return;
      const panelBottom = panel.getBoundingClientRect().bottom;
      const availableBottom = window.innerHeight - 20;
      const hiddenAmount = panelBottom - availableBottom;
      if (hiddenAmount > 0) {
        const distance = hiddenAmount + 16;
        const startY = window.scrollY;
        const duration = 700;
        const startTime = window.performance.now();
        const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

        if (reduceMotion) {
          window.scrollTo(0, startY + distance);
          return;
        }

        const animateScroll = (currentTime) => {
          const progress = Math.min((currentTime - startTime) / duration, 1);
          const easedProgress = 1 - Math.pow(1 - progress, 3);
          window.scrollTo(0, startY + distance * easedProgress);
          if (progress < 1) scrollFrameId = window.requestAnimationFrame(animateScroll);
        };

        scrollFrameId = window.requestAnimationFrame(animateScroll);
      }
    }, 320);

    return () => {
      window.clearTimeout(measureTimerId);
      if (scrollFrameId) window.cancelAnimationFrame(scrollFrameId);
    };
  }, [messagePurpose]);


  // Load data from Firestore
  useEffect(() => {
    if (!docID) return;
    let isCurrent = true;
    setIsContactsLoading(true);
    getDocument({ docID: docID })
      .then((res) => {
        if (!isCurrent || !res) return;
        let data = res.data();
        const loadedSmsBank = parseInt(String(data.smsBank || "0"), 10) || 0;
        const loadedWhatsAppBank = parseInt(String(data.whatsAppBank || "0"), 10) || 0;
        const loadedMessages = Array.isArray(data.messages) ? data.messages : [];
        setSmsBank(loadedSmsBank);
        setWhatsAppBank(
          loadedWhatsAppBank
        );
        setContactList(Array.isArray(data.contacts) ? data.contacts : []);
        setSeatingTables(Array.isArray(data.seatingTables) ? data.seatingTables : []);
        setMessages(loadedMessages);
        setEventTitle(String(data.title || data.invitationData?.eventOwners || "").trim());
        setMessageBankPurchased(data.messageBankPurchased === true);
        setMessageGiftClaimed(data.messageGiftClaimed === true);
        setRecordEntitlement({
          autopilotAssignment: data.autopilotAssignment || null,
          recordLimit: data.recordLimit ?? null,
          rsvpAccess: data.rsvpAccess || null,
        });
        setMessageGiftEligible(
          loadedSmsBank <= 0 &&
          loadedWhatsAppBank <= 0 &&
          data.messageGiftClaimed !== true &&
          (
            data.messageGiftAdminReset === true ||
            (data.messageBankPurchased !== true && loadedMessages.length === 0)
          )
        );
        if (
          !isReadOnly &&
          (loadedSmsBank > 0 || loadedWhatsAppBank > 0) &&
          data.messageGiftClaimed !== true &&
          data.messageBankPurchased !== true
        ) {
          addDocumentData({ docID, payload: { messageBankPurchased: true } });
        }
        setRsvpOptions(normalizeRsvpOptions(data.invitationData?.additionalRsvpOptions));
        if (data.invitationData?.attachmentData?.url) {
          setInvitationImageUrl(data.invitationData.attachmentData.url);
        }
      })
      .finally(() => {
        if (isCurrent) setIsContactsLoading(false);
      });
    getDeliveryRecords({ docID }).then(async (records) => {
      let nextRecords = records;
      if ((records.voice || []).length > 0) {
        try {
          const idToken = await auth.currentUser?.getIdToken();
          if (idToken) {
            const result = await callEventAutopilot({ idToken, eventId: docID, action: "voiceHistory" });
            const richByOperation = new Map((result.calls || []).map((call) => [call.operationId, call]));
            nextRecords = {
              ...records,
              voice: records.voice.map((record) => ({ ...record, ...(richByOperation.get(record.operationId || record.id) || {}) })),
            };
          }
        } catch (_error) {
          // The terminal Firestore operation remains a useful fallback if rich Voice metadata is unavailable.
        }
      }
      if (isCurrent) setDeliveryRecords(nextRecords);
    });
    return () => {
      isCurrent = false;
    };
  }, [docID, isReadOnly]);

  const handleClaimMessageGift = async () => {
    if (isReadOnly || isClaimingMessageGift) return;
    setIsClaimingMessageGift(true);
    try {
      const result = await claimMessageGift({ docID });
      if (result.status !== "claimed") {
        setMessageGiftEligible(false);
        addAlertText({
          mode: "warning",
          text: result.status === "already-claimed"
            ? "המתנה כבר מומשה בחשבון הזה."
            : "החשבון אינו זכאי כרגע למתנת ההודעות.",
        });
        return;
      }

      setSmsBank(result.smsBank);
      setWhatsAppBank(result.whatsAppBank);
      setMessageGiftClaimed(true);
      setMessageGiftEligible(false);
      setShowGiftCelebration(true);
      window.setTimeout(() => setShowGiftCelebration(false), 3200);
      addAlertText({ mode: "success", text: "המתנה נוספה! 10 הודעות WhatsApp ו־10 הודעות SMS מחכות לכם 🎉" });
    } catch (error) {
      console.error("Unable to claim message gift", error);
      addAlertText({ mode: "danger", text: "לא הצלחנו להוסיף את המתנה כרגע. נסו שוב בעוד רגע." });
    } finally {
      setIsClaimingMessageGift(false);
    }
  };

  const handleExcelFile = async (e) => {
    if (isReadOnly) return;
    const input = e.target;
    const file = input.files[0];
    if (!file) return;
    const fileType = file.name.split(".").pop()?.toLowerCase();
    setExcelImportResult(null);
    if (fileType !== "xlsx") {
      addAlertText({ mode: "danger", text: "יש להעלות קובץ מסוג אקסל בלבד" });
      setExcelImportResult({ status: "error", message: "יש להעלות קובץ XLSX בלבד." });
      input.value = "";
      return;
    }
    setIsImportingExcel(true);
    try {
      const rows = await readXlsxFile(file);
      const parsed = parseContactImportRows(rows);
      if (parsed.errors.length > 0) {
        const details = parsed.errors
          .slice(0, 5)
          .map((error) => `שורה ${error.row}: ${error.reason}`)
          .join(" ");
        const remaining = parsed.errors.length > 5 ? ` ועוד ${parsed.errors.length - 5} שגיאות.` : "";
        const message = `${details}${remaining}`;
        setExcelImportResult({ status: "error", message });
        addAlertText({ mode: "danger", text: "האקסל לא הועלה. יש לתקן את השורות השגויות ולנסות שוב." });
        return;
      }

      const contactsBeforeImport = contactListRef.current;
      const saved = await mergeImportedContacts({ docID, contacts: parsed.contacts });
      setContactList(saved.contacts);
      contactListRef.current = saved.contacts;
      setTotal(saved.contacts.length);

      const previousUsage = getRecordUsage(recordEntitlement, contactsBeforeImport);
      const nextUsage = getRecordUsage(recordEntitlement, saved.contacts);

      const skipped = saved.existingDuplicates + parsed.duplicateRows;
      const message = saved.addedCount === 0
        ? `לא נוספו אנשי קשר חדשים. ${skipped} רשומות כפולות דולגו.`
        : `נוספו ${saved.addedCount} אנשי קשר חדשים.${skipped > 0 ? ` ${skipped} רשומות כפולות דולגו.` : ""}`;
      setExcelImportResult({ status: "success", message });
      addAlertText({ mode: "success", text: message });
      if (nextUsage.overLimit && nextUsage.overage > previousUsage.overage) {
        addAlertText({ mode: "warning", text: recordOverageMessage(nextUsage) });
      }
    } catch (error) {
      console.error("Unable to import Excel contacts", error);
      setExcelImportResult({
        status: "error",
        message: "לא הצלחנו לשמור את אנשי הקשר. הרשימה הקיימת לא שונתה. נסו שוב.",
      });
      addAlertText({ mode: "danger", text: "שמירת אנשי הקשר נכשלה. הרשימה הקיימת לא שונתה." });
    } finally {
      setIsImportingExcel(false);
      input.value = "";
    }
  };

  useEffect(() => {
    if (contactList.length > 0) {
      setTotal(contactList.length);
    }
    contactListRef.current = contactList;
  }, [contactList]);

  useEffect(() => () => {
    Object.values(rowSaveTimersRef.current).forEach(clearTimeout);
    clearTimeout(savedNoticeTimerRef.current);
  }, []);

  const UploadBar = ({ result, isLoading }) => {
    if (isLoading) {
      return (
        <div className="col-12 d-flex flex-row justify-content-center align-content-center align-items-center m-3">
          <span className="spinner-border spinner-border-sm m-2" aria-hidden="true" />
          <div className="m-2">קורא ושומר את אנשי הקשר…</div>
        </div>
      );
    }
    if (!result) return null;
    return (
      <div className="col-12 d-flex flex-row justify-content-center align-content-center align-items-center m-3">
        <img src={result.status === "success" ? "./assets/icons/check.svg" : "./assets/icons/out.svg"} height="30" width="30" alt="" />
        <div className="m-2">
          {result.message}
        </div>
      </div>
    );
  };

  // ── Contact management handlers ──────────────────────────────
  const handleAddContact = async () => {
    if (isReadOnly) return;
    const name = newContactName.trim();
    const phone = normalizePhone(newContactPhone);
    if (!name) { addAlertText({ mode: "danger", text: "יש להזין שם." }); return; }
    if (!phone || !/^\d{9,15}$/.test(phone)) {
      addAlertText({ mode: "danger", text: "יש להזין מספר טלפון תקין (ספרות בלבד)." });
      return;
    }
    const howMany = Number(newContactHowMany);
    if (!Number.isInteger(howMany) || howMany < 1 || howMany > 30) {
      addAlertText({ mode: "danger", text: "יש להזין מספר מוזמנים בין 1 ל-30." });
      return;
    }
    const exists = contactList.some((c) => normalizePhone(c.phoneNumber) === phone);
    if (exists) { addAlertText({ mode: "warning", text: "מספר טלפון זה כבר קיים ברשימה." }); return; }
    const newContact = {
      name,
      phoneNumber: phone,
      status: newContactStatus,
      needsRide: false,
      gift: "0",
      howMany: String(howMany),
      dedication: "",
      tableNum: "",
    };
    let saved;
    try {
      saved = await addContactTransactional({ docID, contact: newContact });
    } catch (error) {
      const duplicate = error?.code === "CONTACT_PHONE_EXISTS";
      addAlertText({
        mode: duplicate ? "warning" : "danger",
        text: duplicate ? "מספר טלפון זה כבר קיים ברשימה." : "לא הצלחנו להוסיף את איש הקשר. נסו שוב.",
      });
      return;
    }
    const updated = saved.contacts;
    contactListRef.current = updated;
    setContactList(updated);
    setTotal(updated.length);
    setNewContactName("");
    setNewContactPhone("");
    setNewContactHowMany("1");
    setNewContactStatus("pending");
    setContactSearch("");
    setLatestAddedContactPhone(phone);
    showSavedConfirmation();
    addAlertText({ mode: "success", text: freeContactsVisibility.isFree && getFreeContactsVisibility(recordEntitlement, updated).isLimited ? "איש הקשר נשמר. אנשי קשר מעבר למגבלת המסלול יוצגו לאחר שדרוג." : `${name} נוסף/ה לרשימה.` });
    const previousUsage = getRecordUsage(recordEntitlement, contactList);
    const nextUsage = getRecordUsage(recordEntitlement, updated);
    if (nextUsage.overLimit && nextUsage.overage > previousUsage.overage) {
      addAlertText({ mode: "warning", text: recordOverageMessage(nextUsage) });
    }
    checkBankWarningForGroup(newContactStatus, updated);
  };

  const handleDeleteContact = async (idx) => {
    if (isReadOnly) return;
    if (freeDeletionBlocked) { addAlertText({ mode: "warning", text: FREE_DELETION_MESSAGE }); return; }
    const contact = contactList[idx];
    if (!contact) return;
    let saved;
    try {
      saved = await deleteContactTransactional({ docID, phoneNumber: contact.phoneNumber });
    } catch (error) {
      addAlertText({ mode: "danger", text: "לא הצלחנו למחוק את איש הקשר. הרשימה לא שונתה." });
      return;
    }
    const updated = saved.contacts;
    contactListRef.current = updated;
    setContactList(updated);
    setTotal(updated.length);
    setDeleteConfirmIdx(null);
    showSavedConfirmation();
    addAlertText({ mode: "success", text: "איש הקשר נמחק." });
  };

  // Only the name is editable: the phone number identifies the guest across
  // delivery history, Autopilot rounds, WhatsApp replies and invitation links.
  const handleSaveContactName = async () => {
    if (isReadOnly || !nameEdit || nameEdit.saving) return;
    const savedContact = contactListRef.current[nameEdit.idx];
    if (!savedContact) { setNameEdit(null); return; }
    const name = nameEdit.value.trim();
    if (!name) { addAlertText({ mode: "danger", text: "יש להזין שם." }); return; }
    if (name === savedContact.name) { setNameEdit(null); return; }
    setNameEdit((prev) => prev && { ...prev, saving: true });
    let saved;
    try {
      saved = await updateContactTransactional({
        docID,
        phoneNumber: savedContact.phoneNumber,
        patch: { name },
      });
    } catch (error) {
      setNameEdit((prev) => prev && { ...prev, saving: false });
      addAlertText({ mode: "danger", text: "לא הצלחנו לשמור את השם. הרשימה לא שונתה." });
      return;
    }
    setNameEdit(null);
    if (!saved?.contacts) return;
    contactListRef.current = saved.contacts;
    setContactList(saved.contacts);
    showSavedConfirmation();
  };

  const showSavedConfirmation = () => {
    clearTimeout(savedNoticeTimerRef.current);
    setShowSavedNotice(true);
    savedNoticeTimerRef.current = setTimeout(() => setShowSavedNotice(false), 1800);
  };

  const persistRowEdit = async (idx, edits) => {
    if (isReadOnly) return;
    const contactsSnapshot = contactListRef.current;
    const savedContact = contactsSnapshot[idx];
    if (!savedContact) return;
    const nextContact = { ...savedContact, ...edits };

    if (!validateContactTableCapacity(idx, nextContact)) {
      setRowEdits((prev) => {
        const next = { ...prev };
        delete next[idx];
        return next;
      });
      return;
    }

    const patch = Object.fromEntries(
      Object.entries(edits).filter(([field, value]) => JSON.stringify(savedContact[field]) !== JSON.stringify(value))
    );
    if (Object.keys(patch).length === 0) return;
    let saved;
    try {
      saved = await updateContactTransactional({
        docID,
        phoneNumber: savedContact.phoneNumber,
        patch,
      });
    } catch (error) {
      if (error?.code === "CONTACT_TABLE_CAPACITY_EXCEEDED") {
        addAlertText({
          mode: "warning",
          text: freeContactsVisibility.isFree ? "אין מספיק מקומות פנויים בשולחן הזה." : `אין מספיק מקומות פנויים בשולחן הזה. יש ${error.availableSeats} מקומות פנויים והאורח צריך ${error.neededSeats} מקומות.`,
        });
      } else {
        addAlertText({ mode: "danger", text: "לא הצלחנו לשמור את השינוי. הרשימה לא שונתה." });
      }
      return;
    }
    const updated = saved.contacts;
    contactListRef.current = updated;
    setContactList(updated);
    setRowEdits((prev) => {
      if (JSON.stringify(prev[idx]) !== JSON.stringify(edits)) return prev;
      const next = { ...prev };
      delete next[idx];
      return next;
    });
    showSavedConfirmation();
    if (nextContact.status !== savedContact.status) {
      checkBankWarningForGroup(nextContact.status, updated);
    }
  };

  const scheduleRowSave = (idx, edits, delay = 0) => {
    clearTimeout(rowSaveTimersRef.current[idx]);
    rowSaveTimersRef.current[idx] = setTimeout(() => {
      delete rowSaveTimersRef.current[idx];
      persistRowEdit(idx, edits);
    }, delay);
  };

  const handleRowEdit = (idx, field, value, { autoSave = true, delay = 0 } = {}) => {
    if (isReadOnly) return;
    const nextEdit = {
      ...(rowEdits[idx] || {
        status: contactList[idx].status,
        howMany: contactList[idx].howMany || "1",
        tableNum: contactList[idx].tableNum || "",
      }),
      [field]: value,
    };
    if (field === "howMany" && String(value) !== String(contactList[idx].howMany || "1")) {
      const nextPartySize = Math.max(1, parseInt(String(value), 10) || 1);
      nextEdit.rsvpDetails = resizeRsvpDetails(contactList[idx].rsvpDetails, nextPartySize);
    }
    setRowEdits((prev) => ({ ...prev, [idx]: nextEdit }));
    if (autoSave) scheduleRowSave(idx, nextEdit, delay);
  };

  const handleStatusChange = (idx, nextStatus) => {
    if (isReadOnly) return;
    const savedContact = contactList[idx];
    const currentEdit = rowEdits[idx];
    const currentStatus = currentEdit?.status ?? savedContact.status;
    const currentTableNum = currentEdit?.tableNum ?? savedContact.tableNum ?? "";

    if (currentStatus === "approved" && nextStatus !== "approved" && currentTableNum) {
      handleRowEdit(idx, "status", nextStatus, { autoSave: false });
      setTableRemovalPrompt({ idx, guestName: savedContact.name });
    } else {
      handleRowEdit(idx, "status", nextStatus);
    }
  };

  const resolveTableRemovalPrompt = (removeFromTable) => {
    if (removeFromTable && tableRemovalPrompt) {
      handleRowEdit(tableRemovalPrompt.idx, "tableNum", "");
    } else if (tableRemovalPrompt) {
      const edits = rowEdits[tableRemovalPrompt.idx];
      if (edits) scheduleRowSave(tableRemovalPrompt.idx, edits);
    }
    setTableRemovalPrompt(null);
  };

  const getTableOccupiedSeats = (tableId, excludeIdx = null) =>
    contactListRef.current.reduce((sum, contact, idx) => {
      if (idx === excludeIdx || contact.tableNum !== tableId) return sum;
      return sum + getPartySize(contact);
    }, 0);

  const validateContactTableCapacity = (idx, updatedContact) => {
    if (!updatedContact.tableNum) return true;
    const table = seatingTables.find((item) => item.id === updatedContact.tableNum);
    if (!table) {
      addAlertText({ mode: "warning", text: "השולחן שנבחר לא קיים יותר." });
      return false;
    }
    const capacity = parseInt(String(table.capacity), 10) || 0;
    const occupiedWithoutContact = getTableOccupiedSeats(table.id, idx);
    const availableSeats = Math.max(capacity - occupiedWithoutContact, 0);
    const neededSeats = getPartySize(updatedContact);
    if (neededSeats > availableSeats) {
      addAlertText({
        mode: "warning",
        text: freeContactsVisibility.isFree ? "אין מספיק מקומות פנויים בשולחן הזה. ניתן לבחור שולחן אחר או להגדיל את מספר המקומות." : `אין מספיק מקומות פנויים בשולחן הזה. יש ${availableSeats} מקומות פנויים והאורח צריך ${neededSeats} מקומות. יש להוריד את האורח מהשולחן או להגדיל כיסאות בלשונית "סידורי הושבה".`,
      });
      return false;
    }
    return true;
  };

  const checkBankWarningForGroup = (groupStatus, contactsSnapshot = contactList) => {
    const pendingMessages = messages.filter(
      (m) => m.status !== "cancelled" && m.status !== "error" && moment(m.timeToSend).isAfter(moment())
    );
    let smsBalance = parseInt(String(smsBank || "0"), 10) || 0;
    let whatsAppBalance = parseInt(String(whatsAppBank || "0"), 10) || 0;

    for (const m of pendingMessages) {
      const targets = m.toGroup === "all" || m.toGroup === groupStatus;
      if (!targets) continue;
      const currentCount = contactsSnapshot.filter(
        (c) => m.toGroup === "all" || c.status === m.toGroup
      ).length;
      const reserved = Math.max(0, parseInt(m.recipientCount, 10) || 0);
      const extraNeeded = Math.max(0, currentCount - reserved);
      const availableBank = m.channel === "whatsapp" ? whatsAppBalance : smsBalance;
      const extraCovered = Math.min(extraNeeded, availableBank);
      const missing = Math.max(0, extraNeeded - extraCovered);

      if (m.channel === "whatsapp") {
        whatsAppBalance -= extraCovered;
      } else {
        smsBalance -= extraCovered;
      }

      if (missing > 0) {
        const channel = m.channel === "whatsapp" ? "וואטסאפ" : "SMS";
        addAlertText({
          mode: "warning",
          text: `⚠️ יש הודעת ${channel} ממתינה לקבוצה זו ואין מספיק קרדיט לאורח/ת החדש/ה. האורח/ת נוסף/ה אך לא יקבל/תקבל את ההודעה.`,
        });
        return;
      }
    }
  };

  const isValidSchedule = (dateVal) => {
    if (dateVal == null || dateVal === "") return false;
    const m = moment(dateVal);
    if (!m.isValid()) return false;
    return m.clone().add(5, "minutes").isAfter(moment());
  };

  const handleDeleteMessage = (index) => {
    if (isReadOnly) return;
    const msg = messages[index];
    const isPast = moment(msg.timeToSend).isBefore(moment());
    if (isPast) return;
    setConfirmDeleteMsgIdx(index);
  };

  const confirmDeleteMessage = async () => {
    if (isReadOnly) return;
    const index = confirmDeleteMsgIdx;
    setConfirmDeleteMsgIdx(null);
    const msg = messages[index];
    const updated = messages.map((m, i) =>
      i === index ? { ...m, status: "cancelled" } : m
    );
    await addDocumentData({ docID, payload: { messages: updated } });
    setMessages(updated);

    const refund = msg.recipientCount || 0;
    if (refund > 0) {
      if (msg.channel === "sms") {
        const newBank = parseInt(String(smsBank), 10) + refund;
        await addDocumentData({ docID, payload: { smsBank: newBank } });
        setSmsBank(newBank);
      } else {
        const newBank = parseInt(String(whatsAppBank), 10) + refund;
        await addDocumentData({ docID, payload: { whatsAppBank: newBank } });
        setWhatsAppBank(newBank);
      }
    }

    addAlertText({ text: "ההודעה בוטלה.", mode: "success" });
  };

  const handlePurposeChange = (purpose) => {
    const config = MESSAGE_PURPOSES[purpose];
    if (!config) return;
    setMessagePurpose(purpose);
    setSmsSendTo(config.recommendedGroup);
    if (purpose === "confirmedReminder") {
      setIncludeTable(seatingTables.length > 0);
      setIncludeTransportation(transportationAvailable);
    } else {
      setIncludeTable(false);
      setIncludeTransportation(false);
    }
  };

  const invitationPreviewLink = `https://invitations.flashback.co.il/invitation/${docID}`;
  const generatedWizardPreviewText = buildWizardMessage({
    purpose: messagePurpose,
    channel: roundChannel,
    eventTitle,
    invitationLink: invitationPreviewLink,
    invitationImageUrl,
    includeTable,
    includeTransportation,
    transportationDetails: transportationDetails.trim(),
  });
  const wizardPreviewText = roundChannel === "sms" && isFreeTextSms
    ? freeTextSms
    : generatedWizardPreviewText;

  useEffect(() => {
    if (!messageSummary || messageSummary.entry.channel !== "sms" || typeof window === "undefined") return undefined;
    if (!window.matchMedia("(max-width: 575px)").matches) return undefined;
    const scrollTimer = window.setTimeout(() => {
      messageSummaryBodyRef.current?.scrollTo({ top: 72, behavior: "smooth" });
    }, 120);
    return () => window.clearTimeout(scrollTimer);
  }, [messageSummary]);

  const handleSave = () => {
    if (legacySchedulingClosed) {
      addAlertText({ text: LEGACY_SCHEDULING_CLOSED_TEXT, mode: "warning" });
      return;
    }
    if (!isValidSchedule(smsDate)) {
      addAlertText({ text: "יש לבחור את הזמן הנוכחי או זמן עתידי.", mode: "warning" });
      return;
    }
    if (!MESSAGE_PURPOSES[messagePurpose]) {
      addAlertText({ text: "יש לבחור את מטרת ההודעה.", mode: "warning" });
      return;
    }
    if (roundChannel === "sms" && isFreeTextSms && freeTextSms.trim().length < 5) {
      addAlertText({ text: "חובה להזין טקסט חופשי", mode: "warning" });
      return;
    }
    if (messagePurpose === "confirmedReminder" && includeTransportation && !transportationDetails.trim()) {
      addAlertText({ text: "יש למלא את פרטי ההסעה שיופיעו בהודעה.", mode: "warning" });
      return;
    }
    const pendingCount = contactList.filter(
      (el) => smsSendTo === "all" || el.status === smsSendTo
    ).length;
    if (pendingCount === 0) {
      addAlertText({ text: "לא ניתן לתזמן הודעה לקבוצה ריקה. יש להוסיף לפחות איש קשר אחד לקבוצה שנבחרה.", mode: "warning" });
      return;
    }
    if (roundChannel === "whatsapp" && !extractUploadcareUUID(invitationImageUrl)) {
      addAlertText({ text: "יש להעלות תמונת הזמנה לפני שליחת הודעות וואטסאפ.", mode: "warning" });
      return;
    }
    if (roundChannel === "sms" && messagePurpose === "confirmedReminder" && !invitationImageUrl) {
      addAlertText({ text: "יש להעלות תמונת הזמנה כדי לצרף את הקישור שלה להודעת ה־SMS.", mode: "warning" });
      return;
    }
    if (!isReadOnly && roundChannel === "sms" && pendingCount > parseInt(String(smsBank), 10)) {
      addAlertText({ text: "כמות ההודעות נמוכה מדי, יש לרכוש קרדיט.", mode: "warning" });
      return;
    }
    if (!isReadOnly && roundChannel === "whatsapp" && pendingCount > parseInt(String(whatsAppBank), 10)) {
      addAlertText({ text: "כמות הודעות הוואטסאפ נמוכה מדי, יש לרכוש קרדיט.", mode: "warning" });
      return;
    }
    const newEntry = {
      messageId: createMessageId(),
      timeToSend: moment(smsDate).format(STORAGE_DATE_FORMAT),
      message: wizardPreviewText,
      messagePurpose,
      eventTitle,
      toGroup: smsSendTo,
      withLink: isFreeTextSms ? false : MESSAGE_PURPOSES[messagePurpose].withLink,
      channel: roundChannel,
      freeText: roundChannel === "sms" && isFreeTextSms,
      recipientCount: pendingCount,
      includeTable: messagePurpose === "confirmedReminder" && includeTable,
      includeTransportation: messagePurpose === "confirmedReminder" && includeTransportation,
      transportationDetails:
        messagePurpose === "confirmedReminder" && includeTransportation
          ? transportationDetails.trim()
          : "",
      invitationImageUrl,
    };
    setMessageSummary({
      entry: newEntry,
      channelLabel: roundChannel === "whatsapp" ? "WhatsApp" : "SMS",
      purposeLabel: MESSAGE_PURPOSES[messagePurpose].label,
      groupLabel: determinStatus(smsSendTo),
      dateLabel: moment(smsDate).format("DD/MM/YYYY"),
      timeLabel: moment(smsDate).format("HH:mm"),
      imageUrl: invitationImageUrl,
      previewText: wizardPreviewText,
    });
  };

  const confirmMessageSend = async () => {
    if (!messageSummary || isReadOnly || isSubmittingMessage || legacySchedulingClosed) return;
    setIsSubmittingMessage(true);
    const selectedSendTime = moment(messageSummary.entry.timeToSend).add(5, "minutes");
    const minimumSendTime = moment().add(5, "minutes");
    const approvedSendTime = moment.max(selectedSendTime, minimumSendTime);
    const newEntry = {
      ...messageSummary.entry,
      timeToSend: approvedSendTime.format(STORAGE_DATE_FORMAT),
    };
    const updated = [...messages, newEntry];
    try {
      await addDocumentData({ docID, payload: { messages: updated } });
      setMessages(updated);

      if (newEntry.channel === "sms") {
        const newBank = parseInt(String(smsBank), 10) - newEntry.recipientCount;
        await addDocumentData({ docID, payload: { smsBank: newBank } });
        setSmsBank(newBank);
      } else {
        const newBank = parseInt(String(whatsAppBank), 10) - newEntry.recipientCount;
        await addDocumentData({ docID, payload: { whatsAppBank: newBank } });
        setWhatsAppBank(newBank);
      }

      setMessageSummary(null);
      setSmsDate(defaultSmsDate());
      setSmsSendTo("all");
      setMessagePurpose("");
      setIncludeTable(false);
      setIsFreeTextSms(false);
      setFreeTextSms("");
      setIncludeTransportation(false);
      addAlertText({ text: "ההודעה נשמרה בהצלחה.", mode: "success" });
      setTimeout(() => {
        onTabChange?.("dashboard");
      }, 1000);
    } finally {
      setIsSubmittingMessage(false);
    }
  };

  const sortedSeatingTables = [...seatingTables].sort((a, b) => {
    const aNum = parseInt(String(a.number), 10);
    const bNum = parseInt(String(b.number), 10);
    if (!Number.isNaN(aNum) && !Number.isNaN(bNum) && aNum !== bNum) return aNum - bNum;
    return String(a.number || "").localeCompare(String(b.number || ""), "he");
  });

  const getDeliveredMessageCount = (contact) =>
    deliveryRecords.sms.filter(p => p === contact.phoneNumber).length +
    deliveryRecords.wa.filter(p => p === contact.phoneNumber).length +
    (deliveryRecords.voice || []).filter((record) => normalizePhone(record.phoneNumber) === normalizePhone(contact.phoneNumber)).length;

  const getTableSortValue = (contact) => {
    const table = seatingTables.find((item) => item.id === contact.tableNum);
    if (!table) return Number.MIN_SAFE_INTEGER;
    const parsed = parseInt(String(table.number), 10);
    return Number.isNaN(parsed) ? Number.MAX_SAFE_INTEGER - 1 : parsed;
  };

  const sortFilteredContacts = (items) => {
    const statusOrder = { approved: 1, pending: 2, notComing: 3 };
    return [...items].sort((a, b) => {
      if (latestAddedContactPhone) {
        const aIsLatest = normalizePhone(a.c.phoneNumber) === latestAddedContactPhone;
        const bIsLatest = normalizePhone(b.c.phoneNumber) === latestAddedContactPhone;
        if (aIsLatest !== bIsLatest) return aIsLatest ? -1 : 1;
      }
      if (contactSort === "status") {
        const statusDiff = (statusOrder[a.c.status] || 9) - (statusOrder[b.c.status] || 9);
        if (statusDiff !== 0) return statusDiff;
      }
      if (contactSort === "table") {
        const tableDiff = getTableSortValue(a.c) - getTableSortValue(b.c);
        if (tableDiff !== 0) return tableDiff;
      }
      if (contactSort === "messages") {
        const messageDiff = getDeliveredMessageCount(b.c) - getDeliveredMessageCount(a.c);
        if (messageDiff !== 0) return messageDiff;
      }
      if (contactSort === "missing") {
        const aIsMissing = hasMissingRsvpAnswers(a.c, rsvpOptions);
        const bIsMissing = hasMissingRsvpAnswers(b.c, rsvpOptions);
        if (aIsMissing !== bIsMissing) return aIsMissing ? -1 : 1;
      }
      return String(a.c.name || "").localeCompare(String(b.c.name || ""), "he");
    });
  };

  const updateContactRsvpDetails = async (idx, nextDetails, { finalize = true, acknowledgeFood = true } = {}) => {
    if (isReadOnly) return;
    const contact = contactListRef.current[idx];
    if (!contact) return;
    const partySize = getPartySize(contact);
    await persistRowEdit(idx, {
      status: contact.status,
      howMany: contact.howMany || "1",
      tableNum: contact.tableNum || "",
      rsvpDetails: finalize
        ? finalizeRsvpDetails(nextDetails, rsvpOptions, partySize, {
            acknowledgeFood,
            markAllAttendeesFoodAnswered: false,
          })
        : normalizeRsvpDetails(nextDetails, partySize),
    });
  };

  const openGuestOptionsModal = (idx, attendeeIndex = null, mode = "attendee") => {
    const contact = contactListRef.current[idx];
    if (!contact) return;
    const partySize = getPartySize(contact);
    setGuestOptionsModal({
      idx,
      attendeeIndex,
      mode,
      contactName: contact.name || "איש קשר",
      partySize,
      details: normalizeRsvpDetails(contact.rsvpDetails, partySize),
      foodWasAnswered: getFoodAnswerState(contact, rsvpOptions) !== "notAnswered",
      foodSelectionChanged: false,
    });
  };

  const saveGuestOptionsModal = async () => {
    if (!guestOptionsModal || isReadOnly || isSavingGuestOptions) return;
    setIsSavingGuestOptions(true);
    try {
      await updateContactRsvpDetails(
        guestOptionsModal.idx,
        guestOptionsModal.details,
        {
          finalize: guestOptionsModal.mode !== "transportation",
          acknowledgeFood: guestOptionsModal.foodWasAnswered || guestOptionsModal.foodSelectionChanged,
        }
      );
      setGuestOptionsModal(null);
      addAlertText({ text: "אפשרויות האורח נשמרו.", mode: "success" });
    } finally {
      setIsSavingGuestOptions(false);
    }
  };

  const hasSeatingTables = seatingTables.length > 0;
  const hasDeliveryHistory =
    (deliveryRecords.sms || []).length > 0 ||
    (deliveryRecords.wa || []).length > 0 ||
    (deliveryRecords.voice || []).length > 0 ||
    (deliveryRecords.skipped || []).length > 0;
  const hasGuestPreferences = hasEnabledRsvpOptions(rsvpOptions);
  const contactGridAreas = [
    "name",
    "phone",
    hasDeliveryHistory ? "messages" : "",
    "status",
    "quantity",
    hasSeatingTables ? "table" : "",
    hasGuestPreferences ? "preferences" : "",
    "actions",
  ].filter(Boolean);
  const contactGridColumns = contactGridAreas.map((area) => ({
    name: "minmax(105px, .9fr)",
    phone: "95px",
    messages: "minmax(170px, 1.45fr)",
    status: "minmax(140px, 1.2fr)",
    quantity: "46px",
    table: "minmax(160px, 1.35fr)",
    preferences: "minmax(125px, 1fr)",
    actions: "72px",
  }[area])).join(" ");
  const contactGridStyle = {
    "--contact-grid-columns": contactGridColumns,
    "--contact-grid-areas": `"${contactGridAreas.join(" ")}"`,
    "--contact-grid-expanded": `"${contactGridAreas.map(() => "expanded").join(" ")}"`,
  };
  const bankShortageWarnings = getBankShortageWarnings();
  const recordUsage = getRecordUsage(recordEntitlement, contactList);

  return (
    <div className="col-12 d-flex flex-column justify-content-center align-items-center">

      {guestOptionsModal && (
        <div
          className="guest-options-modal"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget && !isSavingGuestOptions) setGuestOptionsModal(null);
          }}
        >
          <section
            className="guest-options-modal__dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="guest-options-modal-title"
          >
            <header className="guest-options-modal__header">
              <div>
                <small>{guestOptionsModal.contactName}</small>
                <h3 id="guest-options-modal-title">
                  {guestOptionsModal.mode === "transportation"
                    ? "עריכת אפשרויות הסעה"
                    : `עריכת אפשרויות אורח ${guestOptionsModal.attendeeIndex + 1}`}
                </h3>
              </div>
              <button
                type="button"
                aria-label="סגירה"
                disabled={isSavingGuestOptions}
                onClick={() => setGuestOptionsModal(null)}
              >×</button>
            </header>
            <div className="guest-options-modal__body">
              {guestOptionsModal.mode === "attendee" && (() => {
                const attendee = guestOptionsModal.details.attendees[guestOptionsModal.attendeeIndex];
                const activeLabels = new Set(getAttendeePreferenceLabels(attendee, rsvpOptions));
                const inactiveLabels = getAttendeePreferenceLabels(attendee, rsvpOptions, { includeInactive: true })
                  .filter((label) => !activeLabels.has(label));
                return inactiveLabels.length > 0 ? (
                  <div className="contact-preferences__inactive">
                    <strong>אפשרויות לא פעילות – נשמרו ולא נכללות בחישובים</strong>
                    <span>{inactiveLabels.join(", ")}</span>
                  </div>
                ) : null;
              })()}
              <RsvpAdditionalOptions
                options={rsvpOptions}
                partySize={guestOptionsModal.partySize}
                value={guestOptionsModal.details}
                onChange={(details) => setGuestOptionsModal((current) => ({ ...current, details }))}
                onFoodInteraction={() => setGuestOptionsModal((current) => ({ ...current, foodSelectionChanged: true }))}
                disabled={isReadOnly || isSavingGuestOptions}
                alwaysShowFood
                compact
                showTransportation={guestOptionsModal.mode === "transportation"}
                showAttendees={guestOptionsModal.mode === "attendee"}
                attendeeIndex={guestOptionsModal.attendeeIndex}
              />
            </div>
            <footer className="guest-options-modal__actions">
              <button type="button" className="btn btn-outline-secondary" disabled={isSavingGuestOptions} onClick={() => setGuestOptionsModal(null)}>
                סגירה
              </button>
              {!isReadOnly && (
                <button type="button" className="btn btn-success" disabled={isSavingGuestOptions} onClick={saveGuestOptionsModal}>
                  {isSavingGuestOptions ? "שומר..." : "שמירת שינויים"}
                </button>
              )}
            </footer>
          </section>
        </div>
      )}

      {messageSummary && (
        <div
          className="message-summary-modal"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget && !isSubmittingMessage) setMessageSummary(null);
          }}
        >
          <section
            className="message-summary-modal__dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="message-summary-title"
            dir="rtl"
          >
            <header className="message-summary-modal__header">
              <span className="message-summary-modal__eyebrow">בדיקה לפני שליחה</span>
              <h2 id="message-summary-title">סיכום ההודעה</h2>
              <p>בדקו שכל הפרטים נכונים לפני הוספת ההודעה לשליחה.</p>
            </header>

            <div ref={messageSummaryBodyRef} className="message-summary-modal__body">
              <MessagePreview
                channel={messageSummary.entry.channel}
                imageUrl={messageSummary.imageUrl}
                text={messageSummary.previewText}
                freeTextEnabled={Boolean(messageSummary.entry.freeText)}
              />

              {messageSummary.entry.channel === "sms" && (
                <div className="message-summary-modal__sms-image-note">בהודעות SMS נשלח טקסט בלבד, ללא תמונת ההזמנה.</div>
              )}

              <div className="message-summary-modal__details">
                <div><span>סוג הודעה</span><strong>{messageSummary.channelLabel}</strong></div>
                <div><span>מטרת ההודעה</span><strong>{messageSummary.purposeLabel}</strong></div>
                <div><span>קבוצה</span><strong>{messageSummary.groupLabel}</strong></div>
                <div><span>מספר נמענים</span><strong>{freeContactsVisibility.isLimited ? "לפי קבוצת הנמענים שנבחרה" : messageSummary.entry.recipientCount}</strong></div>
                <div><span>תאריך</span><strong>{messageSummary.dateLabel}</strong></div>
                <div>
                  <span>שעה</span>
                  <strong>{messageSummary.timeLabel}</strong>
                  <small className="message-summary-modal__time-offset">+5 דקות</small>
                </div>
                <div><span>קישור להזמנה</span><strong>{messageSummary.entry.withLink ? "יצורף" : "לא יצורף"}</strong></div>
                {(messageSummary.entry.messagePurpose === "confirmedReminder" || messageSummary.entry.freeText) && (
                  <>
                    <div>
                      <span>מספר שולחן</span>
                      <strong className={messageSummary.entry.freeText ? "message-summary-modal__not-included" : ""}>
                        {messageSummary.entry.freeText
                          ? "לא יצורף"
                          : messageSummary.entry.includeTable
                            ? "יצורף למי ששובץ"
                            : "לא יצורף"}
                      </strong>
                    </div>
                    <div>
                      <span>פרטי הסעה</span>
                      <strong className={messageSummary.entry.freeText ? "message-summary-modal__not-included" : ""}>
                        {messageSummary.entry.freeText
                          ? "לא יצורפו"
                          : messageSummary.entry.includeTransportation
                            ? "יצורפו למי שביקש הסעה"
                            : "לא יצורפו"}
                      </strong>
                    </div>
                  </>
                )}
              </div>

              {isReadOnly && (
                <div className="message-summary-modal__demo-note">
                  במערכת ההדגמה ניתן לצפות בסיכום, אך לא לשלוח הודעות.
                </div>
              )}
            </div>

            <footer className="message-summary-modal__actions">
              <button
                type="button"
                className="message-summary-modal__send"
                disabled={isReadOnly || isSubmittingMessage}
                onClick={confirmMessageSend}
              >
                {isSubmittingMessage ? "שולח..." : "שליחת הודעות"}
              </button>
              <button
                type="button"
                className="message-summary-modal__cancel"
                disabled={isSubmittingMessage}
                onClick={() => setMessageSummary(null)}
              >
                ביטול
              </button>
            </footer>
          </section>
        </div>
      )}

      {confirmDeleteMsgIdx !== null && (
        <div style={{ position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", background: "rgba(0,0,0,0.35)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ background: "#fff", padding: "28px 32px", borderRadius: "10px", boxShadow: "0 4px 24px rgba(0,0,0,0.2)", display: "flex", flexDirection: "column", gap: "16px", minWidth: "260px", textAlign: "center", direction: "rtl" }}>
            <div style={{ fontSize: "17px", fontWeight: "bold" }}>לבטל את התזכורת?</div>
            <div style={{ fontSize: "14px", color: "#666" }}>הקרדיט יוחזר לבנק.</div>
            <div style={{ display: "flex", gap: "12px", justifyContent: "center" }}>
              <button disabled={isReadOnly} onClick={confirmDeleteMessage} style={{ background: "#dc3545", color: "#fff", border: "none", borderRadius: "6px", padding: "8px 24px", fontSize: "15px", cursor: "pointer" }}>בטל תזכורת</button>
              <button onClick={() => setConfirmDeleteMsgIdx(null)} style={{ background: "#6c757d", color: "#fff", border: "none", borderRadius: "6px", padding: "8px 24px", fontSize: "15px", cursor: "pointer" }}>חזור</button>
            </div>
          </div>
        </div>
      )}

      {tableRemovalPrompt !== null && (
        <div className="contact-confirm-modal" role="presentation">
          <div
            className="contact-confirm-modal__dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="table-removal-prompt-title"
          >
            <div id="table-removal-prompt-title" className="contact-confirm-modal__title">
              האם להסיר גם מהשולחן?
            </div>
            <div className="contact-confirm-modal__guest">{tableRemovalPrompt.guestName}</div>
            <div className="contact-confirm-modal__actions">
              <button type="button" className="btn btn-danger" onClick={() => resolveTableRemovalPrompt(true)}>
                כן, להסיר מהשולחן
              </button>
              <button type="button" className="btn btn-outline-secondary" onClick={() => resolveTableRemovalPrompt(false)}>
                לא, להשאיר בשולחן
              </button>
            </div>
          </div>
        </div>
      )}

      {showSavedNotice && (
        <div className="contact-saved-notice" role="status" aria-live="polite">
          <span aria-hidden="true">✓</span> נשמר
        </div>
      )}

      {/* ── Settings tab ────────────────────────────────────────── */}
      {activeTab === "settings" && (isContactsLoading ? (
        <div className="contacts-loading-state" role="status" aria-live="polite">
          <span className="contacts-loading-state__spinner" aria-hidden="true" />
          <span>טוען אנשי קשר</span>
        </div>
      ) : (
        <div className="col-12 d-flex flex-column align-items-center mt-3 border-top pt-3" style={{ direction: "rtl" }}>

          {/* ── Full-width Contact Management panel ── */}
          <div className="contacts-panel col-11 col-xxl-10 d-flex flex-column mb-3">
            {recordUsage.overLimit && (
              <div className="contact-bank-warning" role="alert">
                ⚠️ {recordOverageMessage(recordUsage)}
              </div>
            )}
            {freeDeletionBlocked && (
              <div className="contacts-free-limit contacts-free-limit--reached" role="status">
                <div>
                  <strong>הגעתם למגבלה של 50 אנשי קשר!</strong>
                  <span>המערכת ממשיכה לצבור אישורים אבל הם לא מוצגים לכם בממשק</span>
                </div>
                <button type="button" onClick={() => onTabChange?.("messages")}>שדרוג חבילה</button>
              </div>
            )}

            {freeDeletionBlocked && <div className="contact-bank-warning" role="status">{FREE_DELETION_MESSAGE}</div>}
            {/* Panel header */}
            <div className="contacts-panel__header d-flex flex-row align-items-center justify-content-between">
              <span className="contacts-panel__title">הוספת אנשי קשר / ניהול אנשי קשר</span>
              <div className="contacts-panel__actions d-flex flex-row gap-2 align-items-center">
                <span className="badge bg-secondary">
                  {freeContactsVisibility.isLimited ? "50 רשומות מוצגות" : `${visibleContactList.length} אנשי קשר`}
                </span>
                <button
                  className="btn btn-outline-primary btn-sm"
                  disabled={isReadOnly}
                  onClick={() => { setExcelImportResult(null); setShowExcelModal(true); }}
                >
                  📤 העלאת אקסל
                </button>
                {visibleContactList.length > 0 && (
                  <button
                    className="btn btn-outline-success btn-sm"
                    onClick={() => exportToSpreadsheet(visibleContactList, "רשימת מוזמנים", seatingTables, rsvpOptions)}
                  >
                    📥 הורד אקסל
                  </button>
                )}
              </div>
            </div>

            {/* Add contact form */}
            <div className="contact-add-section d-flex flex-column align-items-center">
              <div className="contact-add-methods">
                <button
                  type="button"
                  className="contact-add-toggle"
                  disabled={isReadOnly}
                  aria-expanded={showAddContactForm}
                  onClick={() => setShowAddContactForm((current) => !current)}
                >
                  <span className="contact-add-toggle__icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24"><circle cx="10" cy="8" r="3" /><path d="M4 19c.5-4 2.4-6 6-6s5.5 2 6 6M18 7v6M15 10h6" /></svg>
                  </span>
                  הוספה ידנית של איש קשר
                </button>
                <button
                  type="button"
                  className="contact-add-toggle contact-whatsapp-import-button"
                  data-demo-allow={isReadOnly ? "true" : undefined}
                  onClick={() => setShowWhatsappContactImportModal(true)}
                >
                  <span className="contact-add-toggle__icon" aria-hidden="true">
                    <WhatsAppIcon size={22} />
                  </span>
                  הוספת אנשי קשר מוואטסאפ
                </button>
              </div>

              {showAddContactForm && (
                <div className="contact-add-card">
                  <div className="contact-add-card__header">
                    <div className="contact-add-card__title">פרטי איש קשר חדש</div>
                    <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setShowAddContactForm(false)}>
                      סגור
                    </button>
                  </div>
                  <div className="contact-add-card__form d-flex flex-row gap-2 align-items-end">
                    <div className="contact-add-card__field contact-add-card__field--wide">
                      <label className="form-label fw-semibold mb-1 small">שם מלא</label>
                      <input
                        className="form-control form-control-sm"
                        disabled={isReadOnly}
                        value={newContactName}
                        onChange={(e) => setNewContactName(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") handleAddContact(); }}
                      />
                    </div>
                    <div className="contact-add-card__field contact-add-card__field--wide">
                      <label className="form-label fw-semibold mb-1 small">מספר טלפון</label>
                      <input
                        className="form-control form-control-sm"
                        disabled={isReadOnly}
                        value={newContactPhone}
                        onChange={(e) => setNewContactPhone(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") handleAddContact(); }}
                      />
                    </div>
                    <div className="contact-add-card__field contact-add-card__field--quantity">
                      <label className="form-label fw-semibold mb-1 small">מספר מוזמנים</label>
                      <input
                        type="number"
                        min="1"
                        max="30"
                        className="form-control form-control-sm text-center"
                        disabled={isReadOnly}
                        value={newContactHowMany}
                        onChange={(e) => setNewContactHowMany(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") handleAddContact(); }}
                      />
                    </div>
                    <div className="contact-add-card__field contact-add-card__field--status">
                      <label className="form-label fw-semibold mb-1 small">סטטוס הגעה</label>
                      <select
                        className="form-select form-select-sm"
                        disabled={isReadOnly}
                        value={newContactStatus}
                        onChange={(e) => setNewContactStatus(e.target.value)}
                      >
                        <option value="pending">ממתינים לתשובה/לא יודעים</option>
                        <option value="approved">אישרו הגעה</option>
                        <option value="notComing">לא מגיעים</option>
                      </select>
                    </div>
                    <button className="btn btn-success btn-sm contact-add-card__button" disabled={isReadOnly} onClick={handleAddContact}>
                      + הוסף
                    </button>
                    <p className="contact-add-card__automation-note">
                      להודעות אוטומטיות: השאירו את הסטטוס ממתינים לתשובה/לא יודעים ומספר מוזמנים 1
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Bank shortage warnings */}
            {!freeContactsVisibility.isLimited && bankShortageWarnings.map(({ index, message: m, missing }) => {
              return (
                <div key={index} className="contact-bank-warning">
                  ⚠️ תזכורות לא ישלחו {missing} הודעות - אין מספיק הודעות {m.channel === "whatsapp" ? "וואטסאפ" : "SMS"} ({m.timeToSend})
                </div>
              );
            })}

            <hr className="contacts-panel__divider" />

            {/* Search bar */}
            {visibleContactList.length > 0 && (
              <div className="contact-tools px-3 py-2">
                <div className="contact-tools__search">
                  <input
                    className="form-control form-control-sm"
                    data-demo-allow="true"
                    placeholder="🔍 חיפוש..."
                    value={contactSearch}
                    onChange={(e) => setContactSearch(e.target.value)}
                  />
                </div>
                <div className="contact-tools__sort">
                  <span>מיין לפי</span>
                  <span className="contact-tools__sort-control">
                    <select
                      className="form-select form-select-sm"
                      data-demo-allow="true"
                      aria-label="מיין לפי"
                      disabled={freeContactsVisibility.isLimited}
                      value={contactSort}
                      onPointerDown={() => setLatestAddedContactPhone("")}
                      onChange={(e) => {
                        setLatestAddedContactPhone("");
                        setContactSort(e.target.value);
                      }}
                    >
                      <option value="">שם</option>
                      <option value="status">סטטוס</option>
                      {hasGuestPreferences && <option value="missing">פרטים חסרים</option>}
                      {hasSeatingTables && <option value="table">מספר שולחן</option>}
                      {hasDeliveryHistory && <option value="messages">קבלת הודעות</option>}
                    </select>
                    {freeContactsVisibility.isLimited && (
                      <button
                        type="button"
                        className="contact-tools__sort-lock"
                        aria-label="מיון רשומות מתאפשר לאחר שדרוג חבילה"
                        onClick={() => addAlertText({ text: "מיון רשומות מתאפשר לאחר שדרוג חבילה", mode: "warning" })}
                      />
                    )}
                  </span>
                </div>
              </div>
            )}

            {/* Column headers */}
            {visibleContactList.length > 0 && (
              <div className="contact-header" style={contactGridStyle}>
                <span className="contact-header__name">שם</span>
                <span className="contact-header__phone">טלפון</span>
                {hasDeliveryHistory && <span className="contact-header__messages">הודעות</span>}
                <span className="contact-header__status">סטטוס הגעה</span>
                <span className="contact-header__quantity">כמות</span>
                {hasSeatingTables && <span className="contact-header__table">שיבוץ שולחן</span>}
                {hasGuestPreferences && <span className="contact-header__preferences">העדפות</span>}
                <span className="contact-header__actions">פעולות</span>
              </div>
            )}

            {/* Contacts list */}
            <div className="contacts-list">
              {visibleContactList.length === 0 ? (
                <div className="text-muted text-center py-4 px-3">
                  אין אנשי קשר עדיין — הוסף ידנית או העלה אקסל.
                </div>
              ) : (() => {
                const term = contactSearch.trim().toLowerCase();
                const filtered = contactList
                  .map((c, idx) => ({ c, idx }))
                  .filter(({ c }) => visibleContactSet.has(c))
                  .filter(({ c }) => !term || String(c.name || "").toLowerCase().includes(term) || String(c.phoneNumber || "").includes(term));
                if (filtered.length === 0) {
                  return <div className="text-muted text-center py-3">לא נמצאו תוצאות.</div>;
                }
                const sortedContacts = freeContactsVisibility.isLimited ? filtered : sortFilteredContacts(filtered);
                return <>
                  {sortedContacts.map(({ c: contact, idx }) => {
                  const edit = rowEdits[idx];
                  const currentStatus = edit ? edit.status : contact.status;
                  const currentHowMany = edit ? edit.howMany : (contact.howMany || "1");
                  const currentTableNum = edit ? edit.tableNum : (contact.tableNum || "");
                  const currentPartySize = getPartySize({ howMany: currentHowMany });
                  const selectedTable = seatingTables.find((table) => table.id === currentTableNum);
                  const smsCount = deliveryRecords.sms.filter(p => p === contact.phoneNumber).length;
                  const waCount = deliveryRecords.wa.filter(p => p === contact.phoneNumber).length;
                  const skippedSms = (deliveryRecords.skipped || []).filter(s => s.phoneNumber === contact.phoneNumber && s.channel === "sms").length;
                  const skippedWa  = (deliveryRecords.skipped || []).filter(s => s.phoneNumber === contact.phoneNumber && s.channel === "whatsapp").length;
                  const voiceHistory = (deliveryRecords.voice || [])
                    .filter((record) => normalizePhone(record.phoneNumber) === normalizePhone(contact.phoneNumber))
                    .sort((left, right) => String(right.completedAt || "").localeCompare(String(left.completedAt || "")));
                  const deliveredTotal = smsCount + waCount;
                  const failedTotal = skippedSms + skippedWa;
                  const missingAnswers = getContactRsvpMissingAnswers({
                    ...contact,
                    status: currentStatus,
                    howMany: currentHowMany,
                  }, rsvpOptions);
                  const hasMissingRsvpAnswers = Object.values(missingAnswers).some((count) => count > 0);
                  const foodAnswerState = getFoodAnswerState(contact, rsvpOptions);
                  const activeFoodOptions = getEnabledFoodOptions(rsvpOptions);
                  const preferenceCount = getContactPreferenceCount(contact, rsvpOptions);
                  const preferenceSummary = currentStatus !== "approved"
                    ? "לא מגיעים כרגע"
                    : activeFoodOptions.length > 0
                      ? foodAnswerState === "notAnswered"
                        ? "העדפות טרם נבחרו"
                        : foodAnswerState === "none"
                          ? "אין העדפות מיוחדות"
                          : `${preferenceCount} אורחים עם העדפות`
                      : rsvpOptions.childMeal.enabled || rsvpOptions.gender.enabled
                        ? `${currentPartySize} אורחים`
                        : "הסעה";
                  return (
                    <div
                      key={idx}
                      className={`contact-row ${hasSeatingTables ? "contact-row--has-tables" : ""}`}
                      style={contactGridStyle}
                    >
                      <div className="contact-row__identity">
                        {(smsCount > 0 || waCount > 0 || skippedSms > 0 || skippedWa > 0 || voiceHistory.length > 0) && (
                          <div className={`contact-row__delivery d-flex flex-row flex-wrap gap-1 ${voiceHistory.length > 0 ? "contact-row__delivery--voice" : ""}`}>
                            <span
                              className={`contact-delivery-chip ${deliveredTotal > 0 && failedTotal > 0 ? "contact-delivery-chip--mixed" : deliveredTotal > 0 ? "contact-delivery-chip--success" : "contact-delivery-chip--danger"}`}
                              aria-label={`נשלחו: SMS ${smsCount}, WhatsApp ${waCount}. לא נשלחו: SMS ${skippedSms}, WhatsApp ${skippedWa}.`}
                            >
                              {smsCount > 0 && <span className="contact-delivery-chip__result contact-delivery-chip__result--sms"><span>SMS</span><strong>✓ {smsCount}</strong></span>}
                              {waCount > 0 && <span className="contact-delivery-chip__result contact-delivery-chip__result--whatsapp"><span>WhatsApp</span><strong>✓ {waCount}</strong></span>}
                              {skippedSms > 0 && <span className="contact-delivery-chip__result contact-delivery-chip__result--failed"><span>SMS נכשל</span><strong>! {skippedSms}</strong></span>}
                              {skippedWa > 0 && <span className="contact-delivery-chip__result contact-delivery-chip__result--failed"><span>WhatsApp נכשל</span><strong>! {skippedWa}</strong></span>}
                              {voiceHistory.length > 0 && <span className="contact-delivery-chip__result contact-delivery-chip__result--voice"><span>Voice</span><strong>{voiceHistory.length}</strong></span>}
                            </span>
                            {voiceHistory.length > 0 && (
                              <details className="contact-voice-history">
                                <summary>היסטוריית שיחות</summary>
                                <div className="contact-voice-history__list">
                                  {voiceHistory.map((record) => (
                                    <article key={record.id || record.operationId}>
                                      <strong>{formatVoiceHistoryDate(record.completedAt)}</strong>
                                      {voiceHistoryLabels(record).map((label) => <span key={label}>{label}</span>)}
                                      {record.rsvpStatus && <span>תוצאת אישור הגעה: {VOICE_RSVP_STATUS_LABELS[record.rsvpStatus] || record.rsvpStatus}</span>}
                                      {record.workflowStatus && <small>סטטוס תהליך: {record.workflowStatus}</small>}
                                      {record.workflowState && <small>מצב תהליך: {record.workflowState}</small>}
                                    </article>
                                  ))}
                                </div>
                              </details>
                            )}
                          </div>
                        )}
                        {nameEdit?.idx === idx ? (
                          <div className="contact-row__name contact-row__name--editing">
                            <input
                              type="text"
                              className="form-control form-control-sm contact-row__name-input"
                              value={nameEdit.value}
                              maxLength={80}
                              autoFocus
                              disabled={nameEdit.saving}
                              aria-label="שם האורח"
                              onChange={(e) => setNameEdit((prev) => prev && { ...prev, value: e.target.value })}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") handleSaveContactName();
                                if (e.key === "Escape") setNameEdit(null);
                              }}
                            />
                            <button type="button" className="btn btn-success btn-sm contact-row__btn" disabled={nameEdit.saving} onClick={handleSaveContactName}>שמור</button>
                            <button type="button" className="btn btn-outline-secondary btn-sm contact-row__btn" disabled={nameEdit.saving} onClick={() => setNameEdit(null)}>ביטול</button>
                          </div>
                        ) : (
                          <div className="contact-row__name">
                            {contact.name}
                            {!isReadOnly && (
                              <button
                                type="button"
                                className="contact-row__name-edit"
                                title="עריכת שם"
                                aria-label={`עריכת השם ${contact.name || ""}`}
                                onClick={() => setNameEdit({ idx, value: contact.name || "", saving: false })}
                              >
                                ✎
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="contact-row__phone">{contact.phoneNumber}</div>
                      <select
                        className="form-select form-select-sm contact-row__status"
                        disabled={isReadOnly}
                        value={currentStatus}
                        onChange={(e) => handleStatusChange(idx, e.target.value)}
                        onPointerDown={() => {
                          document.querySelectorAll(".contact-table-picker[open]").forEach((picker) => {
                            picker.removeAttribute("open");
                          });
                        }}
                        aria-label="סטטוס הגעה"
                      >
                        <option value="pending">ממתינים לתשובה/לא יודעים</option>
                        <option value="approved">אישרו הגעה</option>
                        <option value="notComing">לא מגיעים</option>
                      </select>
                      <input
                        type="number"
                        min="1"
                        disabled={isReadOnly}
                        max="30"
                        className="form-control form-control-sm text-center contact-row__qty"
                        value={currentHowMany}
                        onChange={(e) => handleRowEdit(idx, "howMany", e.target.value, { delay: 650 })}
                        title="כמה אנשים יגיעו"
                        aria-label="כמות אורחים"
                      />
                      {hasSeatingTables && (
                        <details
                          className="contact-table-picker"
                          onToggle={(event) => {
                            if (event.currentTarget.open) {
                              document.querySelectorAll(".contact-row__status").forEach((select) => select.blur());
                              document.querySelectorAll(".contact-table-picker[open]").forEach((picker) => {
                                if (picker !== event.currentTarget) picker.removeAttribute("open");
                              });
                            }
                          }}
                        >
                        <summary>
                          {selectedTable ? (
                            <span className="contact-table-picker__line">
                              שולחן <strong>{selectedTable.number}</strong> · {selectedTable.name}
                            </span>
                          ) : (
                            <span className="contact-table-picker__line">ללא שולחן</span>
                          )}
                        </summary>
                        <div className="contact-table-picker__menu">
                          <button
                            type="button"
                            disabled={isReadOnly}
                            className={`contact-table-picker__option ${currentTableNum ? "contact-table-picker__option--remove" : "contact-table-picker__option--selected contact-table-picker__option--unassigned"}`}
                            onClick={(e) => {
                              handleRowEdit(idx, "tableNum", "");
                              e.currentTarget.closest("details")?.removeAttribute("open");
                            }}
                          >
                            <span className="contact-table-picker__line">
                              ללא שולחן {currentTableNum && "· הסרת השיבוץ הקיים"}
                            </span>
                          </button>
                          {sortedSeatingTables.map((table) => {
                            const capacity = parseInt(String(table.capacity), 10) || 0;
                            const occupiedSeats = visibleContactList.reduce((sum, contact) => sum + (String(contact.tableNum) === String(table.id) ? getPartySize(contact) : 0), 0);
                            const occupiedWithoutContact = getTableOccupiedSeats(table.id, idx);
                            const availableForContact = capacity - occupiedWithoutContact;
                            const isCurrentTable = currentTableNum === table.id;
                            const unavailable = !isCurrentTable && availableForContact < currentPartySize;
                            return (
                              <button
                                type="button"
                                key={table.id}
                                disabled={unavailable || isReadOnly}
                                className={`contact-table-picker__option ${isCurrentTable ? "contact-table-picker__option--selected" : ""}`}
                                onClick={(e) => {
                                  handleRowEdit(idx, "tableNum", table.id);
                                  e.currentTarget.closest("details")?.removeAttribute("open");
                                }}
                              >
                                <span className="contact-table-picker__line">
                                  שולחן <strong>{table.number}</strong> · {table.name} · <strong>{occupiedSeats}</strong> יושבים מתוך <strong>{capacity}</strong> מקומות
                                  {unavailable && <span className="contact-table-picker__unavailable"> · אין מספיק מקום</span>}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                        </details>
                      )}
                      <div className="contact-row__actions">
                          {deleteConfirmIdx === idx ? (
                            <>
                              <button className="btn btn-danger btn-sm contact-row__btn" disabled={isReadOnly || freeDeletionBlocked} onClick={() => handleDeleteContact(idx)}>כן</button>
                              <button className="btn btn-outline-secondary btn-sm contact-row__btn" onClick={() => setDeleteConfirmIdx(null)}>לא</button>
                            </>
                          ) : (
                            <button className="btn btn-outline-danger btn-sm contact-row__btn" disabled={isReadOnly || freeDeletionBlocked} title={freeDeletionBlocked ? FREE_DELETION_MESSAGE : undefined} onClick={() => setDeleteConfirmIdx(idx)}>מחק</button>
                          )}
                      </div>
                      {hasEnabledRsvpOptions(rsvpOptions) && (
                        <>
                          <button
                            type="button"
                            className="contact-preferences-toggle"
                            aria-expanded={openContactPreferencesIdx === idx}
                            aria-label={`${preferenceSummary}${hasMissingRsvpAnswers ? ". חסרים פרטים בהעדפות האורחים" : ""}`}
                            onClick={() => setOpenContactPreferencesIdx((current) => current === idx ? null : idx)}
                          >
                            <span>{preferenceSummary}</span>
                            <span className="contact-preferences-toggle__icons" aria-hidden="true">
                              {hasMissingRsvpAnswers && (
                                <span className="contact-preferences-toggle__alert" title="חסרים פרטים בהעדפות האורחים">⚠</span>
                              )}
                              <b className="contact-preferences__summary-plus">
                                {openContactPreferencesIdx === idx ? "−" : "+"}
                              </b>
                            </span>
                          </button>
                          {openContactPreferencesIdx === idx && (
                          <div className="contact-preferences-expanded">
                            {contact.status === "approved" ? (
                              <>
                                <div className="contact-preferences__summary">
                                  {(rsvpOptions.childMeal.enabled || rsvpOptions.gender.enabled || getEnabledFoodOptions(rsvpOptions).length > 0) &&
                                    normalizeRsvpDetails(contact.rsvpDetails, currentPartySize).attendees.map((attendee, attendeeIndex) => {
                                      const storedAttendee = contact.rsvpDetails?.attendees?.[attendeeIndex];
                                      const labels = getAttendeePreferenceLabels(attendee, rsvpOptions);
                                      const attendeeFoodMissing = activeFoodOptions.length > 0 && (
                                        !contact.rsvpDetails?.completedAt ||
                                        attendee.foodAnswered !== true ||
                                        activeFoodOptions.some((key) => !attendee.answeredFoodOptions.includes(key))
                                      );
                                      const displayLabels = attendeeFoodMissing
                                        ? [...labels.filter((label) => label !== "מנה רגילה"), "העדפות מזון טרם נבחרו"]
                                        : labels;
                                      const attendeeMissingLabels = [
                                        missingAnswers.childMeal > 0 && (!contact.rsvpDetails?.completedAt || typeof storedAttendee?.childMeal !== "boolean")
                                          ? "מנות ילדים"
                                          : "",
                                        missingAnswers.gender > 0 && (!contact.rsvpDetails?.completedAt || !["male", "female"].includes(attendee.gender))
                                          ? "גבר או אישה"
                                          : "",
                                        attendeeFoodMissing ? "העדפות מזון" : "",
                                      ].filter(Boolean);
                                      const assumedDish = getAttendeeExactDish(attendee, storedAttendee, rsvpOptions);
                                      return (
                                        <button
                                          type="button"
                                          className="contact-preferences__guest-card"
                                          key={attendee.id || attendeeIndex}
                                          onClick={() => openGuestOptionsModal(idx, attendeeIndex, "attendee")}
                                        >
                                          {attendeeMissingLabels.length > 0 && (
                                            <span
                                              className="contact-preferences__card-alert"
                                              title={`חסרה בחירה: ${attendeeMissingLabels.join(", ")}`}
                                              aria-label={`חסרה בחירה: ${attendeeMissingLabels.join(", ")}`}
                                            >!</span>
                                          )}
                                          <span className="contact-preferences__click-icon" aria-hidden="true">+</span>
                                          <strong>אורח {attendeeIndex + 1}</strong>
                                          <span>{displayLabels.length > 0 ? displayLabels.join(", ") : "אין העדפות מיוחדות"}</span>
                                          {attendeeMissingLabels.length > 0 && (
                                            <small className="contact-preferences__missing-text">
                                              חסר: {attendeeMissingLabels.join(", ")}
                                              {assumedDish.label && <> · בדוח יחושב כברירת מחדל: {assumedDish.label}</>}
                                            </small>
                                          )}
                                        </button>
                                      );
                                    })}
                                  {rsvpOptions.transportation.enabled && (
                                    <button
                                      type="button"
                                      className="contact-preferences__guest-card"
                                      onClick={() => openGuestOptionsModal(idx, null, "transportation")}
                                    >
                                      {missingAnswers.transportation > 0 && (
                                        <span
                                          className="contact-preferences__card-alert"
                                          title="חסרה תשובת הסעה"
                                          aria-label="חסרה תשובת הסעה"
                                        >!</span>
                                      )}
                                      <span className="contact-preferences__click-icon" aria-hidden="true">+</span>
                                      <strong>הסעה</strong>
                                      <span>
                                        {!contact.rsvpDetails?.transportation?.answered
                                          ? "לא נבחרה תשובה"
                                          : contact.rsvpDetails.transportation.needsRide
                                            ? `כן, ${contact.rsvpDetails.transportation.seats || 0} מקומות`
                                            : "לא"}
                                      </span>
                                      {missingAnswers.transportation > 0 && (
                                        <small className="contact-preferences__missing-text">
                                          חסר: תשובת הסעה · בדוח יחושב כברירת מחדל: ללא הסעה
                                        </small>
                                      )}
                                    </button>
                                  )}
                                </div>
                              </>
                            ) : (
                              <p className="contact-preferences__empty">האפשרויות הפעילות יחולו כאשר סטטוס איש הקשר יהיה “אישרו הגעה”.</p>
                            )}
                          </div>
                          )}
                        </>
                      )}
                    </div>
                  );
                  })}
                  {freeContactsVisibility.isLimited && [0, 1].map((placeholderIndex) => (
                    <div className="contact-locked-placeholder" key={`locked-${placeholderIndex}`}>
                      <span aria-hidden="true">🔒</span>
                      <strong>שאר הרשומות ייפתחו לאחר שדרוג החבילה.</strong>
                    </div>
                  ))}
                </>;
              })()}
            </div>
          </div>

          {/* ── Excel Upload Modal ── */}
          {showExcelModal && (
            <div
              className="modal d-block"
              style={{ background: "rgba(0,0,0,0.5)" }}
              onClick={(e) => { if (!isImportingExcel && e.target === e.currentTarget) setShowExcelModal(false); }}
            >
              <div className="modal-dialog modal-dialog-centered" style={{ direction: "rtl", maxWidth: "460px" }}>
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title fw-bold">העלאת קובץ אקסל</h5>
                    <button
                      type="button"
                      className="btn-close"
                      disabled={isImportingExcel}
                      style={{ marginRight: "auto", marginLeft: 0 }}
                      onClick={() => setShowExcelModal(false)}
                    />
                  </div>
                  <div className="modal-body">
                    <div className="d-flex flex-column uploadBox justify-content-center align-items-center fs-5 greyBlueText rounded-3 bg-light p-3">
                      לחצו כאן להעלות את קובץ רשימת האורחים
                      <div className="p-2 fs-6 text-center text-success">
                        אנשי קשר חדשים יתווספו לרשימה הקיימת — ללא מחיקת נתונים.
                      </div>
                      <a href="./assets/files/אקסל מוזמנים לדוגמא.xlsx" className="fs-6">
                        לחצו כאן על מנת להוריד קובץ לדוגמא
                      </a>
                      <img src="./assets/icons/upload.svg" height="50" width="50" alt="" className="my-2" />
                      <input
                        className="form-control col-10"
                        type="file"
                        disabled={isReadOnly || isImportingExcel}
                        accept=".xlsx"
                        placeholder="בחר קובץ"
                        onChange={handleExcelFile}
                      />
                    </div>
                    <UploadBar result={excelImportResult} isLoading={isImportingExcel} />
                  </div>
                  <div className="modal-footer">
                    <button className="btn btn-secondary" disabled={isImportingExcel} onClick={() => setShowExcelModal(false)}>סגור</button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {showWhatsappContactImportModal && (
            <div
              className="modal d-block whatsapp-contact-import-modal"
              style={{ background: "rgba(0,0,0,0.56)" }}
              onClick={(event) => {
                if (event.target === event.currentTarget) setShowWhatsappContactImportModal(false);
              }}
            >
              <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable" style={{ direction: "rtl", maxWidth: "590px" }}>
                <div className="modal-content whatsapp-contact-import-modal__content">
                  <div className="modal-header whatsapp-contact-import-modal__header">
                    <div className="d-flex align-items-center gap-2">
                      <span className="whatsapp-contact-import-modal__logo"><WhatsAppIcon size={25} /></span>
                      <div>
                        <h5 className="modal-title fw-bold mb-1">הוספת אנשי קשר מוואטסאפ</h5>
                        <div className="small text-muted">שליחת אנשי קשר מהטלפון ובדיקה ידנית של הרשימה</div>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="btn-close"
                      style={{ marginRight: "auto", marginLeft: 0 }}
                      onClick={() => setShowWhatsappContactImportModal(false)}
                      aria-label="סגירה"
                    />
                  </div>

                  <div className="modal-body whatsapp-contact-import-modal__body">
                    <p className="mb-3">
                      ניתן לשלוח אנשי קשר ישירות מהטלפון לבוט הוואטסאפ שלנו. לאחר סיום השליחה,
                      הרשימה תעבור לבדיקה ותועלה לאירוע שלכם במרוכז.
                    </p>

                    {whatsappContactImportEligible && (
                      <div className="whatsapp-contact-import-modal__eligibility is-eligible">
                        השירות זמין בחשבון שלכם. פתיחת השיחה ושליחת אנשי הקשר אינן מפחיתות מיתרת ההודעות.
                      </div>
                    )}

                    <h6 className="fw-bold mt-4 mb-2">איך שולחים את אנשי הקשר?</h6>
                    <ol className="whatsapp-contact-import-modal__steps">
                      <li>לחצו למטה על <strong>התחלת שיחה עם בוט הוואטסאפ</strong>.</li>
                      <li>בוואטסאפ לחצו על סימן הפלוס או על סמל הצירוף.</li>
                      <li>בחרו <strong>איש קשר</strong> או <strong>אנשי קשר</strong>.</li>
                      <li>סמנו יחד את כל אנשי הקשר שברצונכם לצרף ושלחו אותם.</li>
                      <li>אפשר לשלוח כמה קבוצות של אנשי קשר שצריך.</li>
                      <li>אחרי שסיימתם לשלוח את כולם, כתבו בדיוק: <strong>{WHATSAPP_CONTACT_COMPLETION_PHRASE}</strong></li>
                    </ol>

                    <div className="whatsapp-contact-import-modal__info-boxes">
                      <div className="whatsapp-contact-import-modal__review-note whatsapp-contact-import-modal__review-note--green">
                        לאחר סיום העלאת כל אנשי הקשר - יש לרשום “{WHATSAPP_CONTACT_COMPLETION_PHRASE}”. הרשימה תעבור בדיקה ידנית ותועלה עד 2 ימי עבודה.
                      </div>
                      <div className="whatsapp-contact-import-modal__review-note whatsapp-contact-import-modal__review-note--purple">
                        לאחר העלאת הרשימה והאישור, ניתן להוסיף אנשי קשר יחידים בלשונית ‘הוספה ידנית של איש קשר’ בכדי לקלוט אותם מיידית.
                      </div>
                    </div>

                    {whatsappContactImportEligible && !eventOwnerEmail && (
                      <div className="alert alert-danger mt-3 mb-0">
                        לא ניתן לזהות את אימייל בעל האירוע. יש להתחבר מחדש לפני פתיחת השיחה.
                      </div>
                    )}
                  </div>

                  <div className="modal-footer whatsapp-contact-import-modal__footer">
                    <button className="btn btn-light" onClick={() => setShowWhatsappContactImportModal(false)}>סגירה</button>
                    {whatsappContactImportEligible ? (
                      <a
                        className={`btn whatsapp-contact-import-modal__start ${!eventOwnerEmail ? "disabled" : ""}`}
                        href={eventOwnerEmail ? whatsappContactBotUrl : undefined}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-disabled={!eventOwnerEmail}
                        onClick={() => {
                          if (eventOwnerEmail) setShowWhatsappContactImportModal(false);
                        }}
                      >
                        <WhatsAppIcon size={20} />
                        התחלת שיחה עם בוט הוואטסאפ
                      </a>
                    ) : (
                      <button className="btn whatsapp-contact-import-modal__start" type="button" disabled>
                        <WhatsAppIcon size={20} />
                        נדרשת חבילת הודעות
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

        </div>
      ))}

      {/* ── Messages tab ─────────────────────────────────────────── */}
      {activeTab === "messages" && (
        <div className="col-10 d-flex flex-column justify-content-center align-items-center border-top mt-3 toHide">
          {!isContactsLoading && showAutopilotPurchase && (
            <div className="legacy-commerce-entry col-12">
              <CommercePackagePicker eventId={docID} disabled={isReadOnly} freeLimitReached={freeDeletionBlocked} />
            </div>
          )}
          {showGiftCelebration && (
            <div className="message-gift-celebration" role="status" aria-live="polite">
              <div className="message-gift-celebration__confetti" aria-hidden="true">
                {Array.from({ length: 28 }, (_, index) => (
                  <i
                    key={index}
                    style={{
                      left: `${(index * 37) % 100}%`,
                      animationDelay: `${(index % 9) * 0.08}s`,
                      animationDuration: `${1.7 + (index % 5) * 0.16}s`,
                      backgroundColor: ["#ffcc4d", "#ff6b91", "#39c6a4", "#6e7bf2", "#ff8a3d"][index % 5],
                    }}
                  />
                ))}
              </div>
              <div className="message-gift-celebration__message">
                <span aria-hidden="true">🎉</span>
                <strong>המתנה שלכם כבר כאן!</strong>
                <small>10 הודעות WhatsApp ו־10 הודעות SMS נוספו לבנק.</small>
              </div>
            </div>
          )}
          <div className="fs-3 text-muted mb-2">שליחת הודעות אוטומטיות לאורחים</div>

          {/* ── Message log ── */}
          <MessageScheduleList
            messages={messages}
            hideContactCounts={freeContactsVisibility.isLimited}
            deliveryRecords={deliveryRecords}
            invitationImageUrl={invitationImageUrl}
            getMissingCount={(message) => bankShortageWarnings.find((warning) => warning.message === message)?.missing || 0}
            disabled={isReadOnly}
            onDelete={handleDeleteMessage}
          />

          {showLegacyClosedNotice && (
            <div className="legacy-scheduling-closed col-12" role="status" dir="rtl">
              {LEGACY_SCHEDULING_CLOSED_TEXT}
            </div>
          )}

          {/* ── Single message form ── */}
          {!legacySchedulingClosed && (
          <div className="col-12 d-flex flex-column justify-content-center align-items-center shadow-lg m-2 message-schedule-card rounded border border-3">
            {hasPositiveLegacyMessageBank && (
              <RoundChannelToggle
                value={roundChannel}
                onChange={setRoundChannel}
                disabled={false}
                smsAvailable={smsBankAvailable}
                whatsappAvailable={whatsAppBankAvailable}
                smsBank={smsBank}
                whatsAppBank={whatsAppBank}
                giftEligible={messageGiftEligible && !isReadOnly}
                giftDismissed={messageGiftDismissed}
                isClaimingGift={isClaimingMessageGift}
                onClaimGift={handleClaimMessageGift}
                onDismissGift={() => setMessageGiftDismissed(true)}
              />
            )}
            <div
              className="col-12 d-flex flex-column justify-content-center align-items-center"
              style={
                !smsBankAvailable && !whatsAppBankAvailable
                  ? { pointerEvents: "none", opacity: 0.35, userSelect: "none" }
                  : {}
              }
            >
            {hasPositiveLegacyMessageBank && (
              <MessagePurposeWizard
                value={messagePurpose}
                onChange={handlePurposeChange}
                disabled={false}
              />
            )}

            {messagePurpose && (
              <>
            <div className="message-compose-layout" dir="rtl">
              <div className="message-compose-layout__controls">
                <Timing
                  isDisabled={false}
                  demoAllow={isReadOnly}
                  currentName={"smsSendToGroup"}
                  key="smsSendToGroup"
                  handleChange={setSmsSendTo}
                  checked={smsSendTo}
                  counts={groupCounts}
                />

            <div className="message-compose-layout__datetime col-12 d-flex flex-row justify-content-center align-items-stretch gap-3 p-2 mx-auto">
              {/* שעה — left column */}
              <div
                className="datetime-col datetime-col--time d-flex flex-column align-items-center justify-content-center rounded p-2"
                data-demo-allow={isReadOnly ? "true" : undefined}
                style={{ flex: "1 1 0" }}
              >
                <label className="fw-bold mb-1 datetime-col__label datetime-col__label--time">שעה</label>
                <DatePicker
                  selected={smsDate}
                  data-demo-allow={isReadOnly ? "true" : undefined}
                  onChange={(time) => {
                    if (!time) return;
                    const d = new Date(smsDate);
                    d.setHours(time.getHours(), time.getMinutes(), 0, 0);
                    setSmsDate(d);
                  }}
                  locale="he"
                  showTimeSelectOnly
                  timeIntervals={15}
                  timeFormat="HH:mm"
                  dateFormat="HH:mm"
                  placeholderText="בחר שעה"
                  className="form-control datetime-picker-input text-center"
                  wrapperClassName="w-100"
                />
              </div>
              {/* תאריך — right column */}
              <div
                className="datetime-col datetime-col--date d-flex flex-column align-items-center justify-content-center rounded p-2"
                data-demo-allow={isReadOnly ? "true" : undefined}
                style={{ flex: "1 1 0" }}
              >
                <label className="fw-bold mb-1 datetime-col__label datetime-col__label--date">תאריך</label>
                <EventDatePicker
                  id="message-send-date"
                  value={getLocalIsoDate(smsDate)}
                  onChange={(isoDate) => {
                    if (!isoDate) return;
                    const [year, month, day] = isoDate.split("-").map(Number);
                    const d = new Date(smsDate);
                    d.setFullYear(year, month - 1, day);
                    setSmsDate(d);
                  }}
                  placeholderText="בחר תאריך"
                  ariaLabel="תאריך שליחה"
                />
              </div>
            </div>

                {messagePurpose === "confirmedReminder" && (
                  <section ref={confirmedReminderOptionsRef} className="confirmed-reminder-options" dir="rtl" aria-labelledby="confirmed-reminder-options-title">
                    <h3 id="confirmed-reminder-options-title">מה לצרף לתזכורת?</h3>
                    <p>הפרטים יתווספו רק למוזמנים שעבורם קיים המידע המתאים.</p>
                    <div className="confirmed-reminder-options__switches">
                      <label className={`confirmed-reminder-option ${seatingTables.length === 0 ? "confirmed-reminder-option--disabled" : ""}`}>
                        <input
                          type="checkbox"
                          checked={includeTable}
                          disabled={seatingTables.length === 0}
                          data-demo-allow={isReadOnly ? "true" : undefined}
                          onChange={(event) => setIncludeTable(event.target.checked)}
                        />
                        <span>
                          <strong>שליחת מספר שולחן</strong>
                          <small>{seatingTables.length > 0 ? "יצורף רק למוזמנים ששובצו לשולחן" : "לא הוגדרו שולחנות באירוע"}</small>
                        </span>
                      </label>
                      <label className={`confirmed-reminder-option ${!transportationAvailable ? "confirmed-reminder-option--disabled" : ""}`}>
                        <input
                          type="checkbox"
                          checked={includeTransportation}
                          disabled={!transportationAvailable}
                          data-demo-allow={isReadOnly ? "true" : undefined}
                          onChange={(event) => setIncludeTransportation(event.target.checked)}
                        />
                        <span>
                          <strong>שליחת פרטי הסעה</strong>
                          <small>
                            {!rsvpOptions.transportation.enabled
                              ? "אפשרות ההסעה אינה פעילה בהגדרות האירוע"
                              : !transportationDetails
                                ? "יש למלא כתובת ושעת יציאה בהגדרות האירוע"
                                : "יצורפו רק למוזמנים שביקשו הסעה"}
                          </small>
                        </span>
                      </label>
                    </div>
                    {includeTransportation && (
                      <label className="confirmed-reminder-options__transportation">
                        <span>פרטי ההסעה שיופיעו בהודעה</span>
                        <input
                          type="text"
                          value={transportationDetails}
                          readOnly
                          aria-readonly="true"
                        />
                        <small>הפרטים נלקחים מאופציות לשאלות נוספות לאורחים.</small>
                      </label>
                    )}
                  </section>
                )}

              </div>

              <div className="message-compose-layout__preview">
                <MessagePreview
                  channel={roundChannel}
                  imageUrl={invitationImageUrl}
                  text={wizardPreviewText}
                  freeTextEnabled={isFreeTextSms}
                  onFreeTextToggle={(enabled) => {
                    setIsFreeTextSms(enabled);
                    if (!enabled) setFreeTextSms("");
                  }}
                  onTextChange={setFreeTextSms}
                />
                <button className="btn btn-lg btn-danger message-send-button" onClick={handleSave}>
                  לחצו כאן לשליחה
                </button>
              </div>
            </div>
              </>
            )}
            </div> {/* end dimmed wrapper */}
          </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Settings;
